/** Generate Image — Canonical Generation Card
 * (VISTOERA_GENERATE_IMAGE_CANONICAL_GENERATION_CARD_RESULT_FIRST_EXTERNAL_QUICK_ACTIONS_V3)
 *
 * Vertical order (HARD): Header → Result → Related Assets → Parameters → Prompt → Footer.
 * Collapsed: Header + Result only. External Quick Actions = CARD RIGHT OUTSIDE
 * (`MediaWorkflowRail`) on Result hover with hover-bridge — never inside Result/Card content.
 *
 * Generation Provider / Worker / Flux runtime / task lifecycle unchanged.
 */

import {
  CloseOutlined,
  CopyOutlined,
  DownloadOutlined,
  DownOutlined,
  EditOutlined,
  HistoryOutlined,
  LoadingOutlined,
  PictureOutlined,
  PushpinFilled,
  PushpinOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { Handle, Position, useConnection, useUpdateNodeInternals, type NodeProps } from '@xyflow/react'
import { Button, Tooltip, message } from 'antd'
import CanvasSelect from '../CanvasSelect'
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import {
  getAuthMatrixRevision,
  getAuthMatrixStatus,
  isChannelEffectiveAllowed,
  isNodeAuthDenied,
  subscribeAuthMatrix,
} from '../authMatrixRuntime'
import { useCanvasNodeRegistryI18n } from '../nodeRegistryI18n'
import { useCanvasGenerationI18n } from '../useCanvasGenerationI18n'
import CanvasActionPopover from '../CanvasActionPopover'
import {
  createImageGenerationTask,
  getImageGenerationTask,
  imageGenerationOutputContentUrl,
  materializeImageGenerationOutput,
  type ImageGenerationTask,
  type SaasApiError,
} from '../../../../api'
import { useNodeDefinition } from '../nodeRegistry'
import MediaLightbox from '../MediaLightbox'
import ImageEditorErrorBoundary from './ImageEditorErrorBoundary'
import { createEditorDiagnosticsSnapshot, type EditorDiagnosticsRef } from '../../../../../components/editor/editorDiagnostics'
import { resolveGenerateImageEditSource } from '../canvasGenerateImageEditEntry'
import { useSingleOrDoubleClick } from '../useSingleOrDoubleClick'
import { downloadCanvasMedia } from '../canvasDownload'
import { resolveCanvasImageThumbUrl, resolveImageGenCanvasDisplayUrl, resolveImageGenMasterUrl } from '../canvasImageQuality'
import { useCanvasMediaViewportGate, useCanvasProgressiveImageSrc } from '../useCanvasProgressiveImage'
import {
  computeGenerationDriftState,
  computeGenerationInputFingerprint,
  computeImageConfigFingerprint,
  computeImageUpstreamFingerprint,
  generationDriftInspectorText,
  legacyImageReferenceAssetIds,
} from '../generationInputFingerprint'
import {
  computeMaterialAliases,
  getNodeIO,
  GENERIC_INPUT_HANDLE,
  IMAGE_PROMPT_IN_HANDLE,
  imageReferenceAssetHandleId,
  nodeOutputDataTypes,
  type DataType,
} from '../dataflow'
import { countDisconnectedMentions, resolvePromptForSubmit } from '../assetMention'
import ImageRelatedAssetsSection from './ImageRelatedAssetsSection'
import AssetMentionPromptEditor from './AssetMentionPromptEditor'
import ImagePromptTranslateTools from './ImagePromptTranslateTools'
import VideoInputSectionHandle from './VideoInputSectionHandle'
import { imageProviderRecommendsPromptTranslation } from '../imagePromptTranslation'
import MediaWorkflowRail from './MediaWorkflowRail'
import { useMaterialThumbMap } from './useMaterialThumbMap'
import {
  resolveGenerationDisplayOutputId,
  shouldShowActiveGenerationOutput,
} from '../canvasGenerationActivePreview'
import { deriveCurrentGenerationPresentation } from '../canvasGenerationPresentation'
import { patchCanvasFieldsIfChanged } from '../canvasFieldsEqual'
import {
  generationFooterRunLabel,
  generationResultPlaceholderLabel,
} from '../canvasGenerationStatusLabels'
import { useCanvasGenerationSubmission } from '../useCanvasGenerationSubmission'
import {
  countImageReferenceMaterials,
  evaluateImageReferenceLimit,
  relatedAssetsAddLimitMessage,
  referenceLimitValidationMessage,
  resolveCapabilityForGenerateImageNode,
} from '../imageGenerationReferenceCapability'
import {
  evaluateImagePromptLimit,
  promptLimitValidationMessage,
  resolvePromptConstraintsForGenerateImageNode,
} from '../imageGenerationPromptCapability'
import {
  localizeImageGenerationSubmitApiError,
  localizeImageGenerationTaskError,
} from '../imageGenerationError'
import {
  buildImageGenerationSubmitPayload,
  hasUnresolvedExplicitProviderChannel,
  imageProviderExecutionBlockMessage,
  isImageProviderExecutionSupported,
  PROVIDER_CHANNEL_LEGACY,
  parseProviderChannels,
  authFilteredImageProviderChannels,
  resolveCanvasImageTaskFieldStatus,
  resolveDefaultProviderChannelId,
  resolveProviderChannel,
  resolveSubmitProviderChannel,
} from '../imageGenerationProvider'
import {
  IMAGE_GENERATION_BATCH_COMMITTED_FIELD,
  IMAGE_GENERATION_BATCH_ERROR_SUMMARY_FIELD,
  buildImageGenerationSlotTaskFieldsPatch,
  imageGenerationBatchSlotIndex,
  isImageGenerationBatchFollower,
  isImageGenerationBatchPollLeader,
  isImageGenerationResultSlotRunning,
  isImageGenerationSlotStatusRunning,
  pickImageGenerationOutputForSlot,
  requestedImageGenerationBatchCount,
  resetImageGenerationBatchFieldsPatch,
} from '../imageGenerationBatchCanvas'
import {
  buildImageProviderSelectionPatch,
  buildImageProviderSwitchExecutionResetPatch,
  isImageGenerationRunOwnedByCurrentProvider,
  nextImageGenerationAttemptId,
  shouldClearImageGenerationFailedRunIdentity,
  shouldDiscardImageGenerationAttempt,
} from '../imageGenerationProviderSwitch'
import {
  assertProviderSupportsProductionAspect,
  buildKeyframeImageSourceContext,
  deriveProductionFormatFromVideoFields,
  readProductionFormat,
  resolveCanonicalProductionTarget,
  resolveKeyframeFormatSource,
  resolveProviderNativeSizeForAspect,
} from '../storyboardProductionFormat'
import {
  buildReferenceBindings,
  inferImageGenerationOperation,
  readReferenceBindingsFromFields,
  serializeReferenceAssetRefByEdgeId,
} from '../referenceImageInputs'
import {
  relatedAssetsDropZoneClassName,
  resolveGenerateImageInputHandleVisibility,
} from '../generateImageInputHandleVisibility'
import { functionCardShowsInspector } from '../functionCardInteractionV2'
import { NODE_EXPAND_TRIGGER_CLASS, canvasNodeShellStateClass } from '../canvasNodeShellV2'
import { ratioForLayout, ratioForSubmit, resolveRatioChoice } from '../imageGenerationRatio'
import ImageAspectRatioSelect from './ImageAspectRatioSelect'
import { generationSlotIngressShellClass } from '../generationSlotIngress'
import { generationNodeCardWidth } from '../generationNodeWidthPresets'
import CanvasFunctionCardHeader from '../CanvasFunctionCardHeader'
import { usePinnedExpandedNode } from '../useCanvasNodePin'
import {
  computeImageSuccessDisplaySize,
  imageSuccessContentWidth,
  imageSuccessResultAreaHeight,
  IMAGE_BUSY_PLACEHOLDER_MIN_HEIGHT_EXPANDED,
  IMAGE_SUCCESS_RESULT_MAX_HEIGHT_COLLAPSED,
  IMAGE_SUCCESS_RESULT_MAX_HEIGHT_EXPANDED,
} from '../imageGenerationResultLayout'
import {
  resolveCollapsedMediaCarrierSize,
  resolveCollapsedMediaPresentationSize,
  resolveGenerationCardWidth,
} from '../canvasCollapsedResultFrameContract'
import { computeMediaBoxSize } from '../mediaBoxSize'
import type { FlowNodeData } from '../types'

// VistoeraTASK-00251 — "编辑图片" opens the EXACT SAME canonical Canvas
// Image Editor overlay `ImageAssetNode.tsx` already uses (shared
// `DualPaneCutoutEditor` core underneath, same "另存为新资产" save
// pipeline) — never a second/cloned editor. Lazy (dynamic-import) so this
// heavy chunk is never part of Canvas's initial bundle/request graph;
// Vite/Rollup dedupes this against `ImageAssetNode.tsx`'s identical lazy
// import of the same module into ONE shared chunk, loaded on demand only
// the first time EITHER node type's "编辑图片" is actually clicked.
const CanvasImageEditorOverlay = lazy(() => import('./CanvasImageEditorOverlay'))

const POLL_INTERVAL_MS = 2500
const HEADER_HEIGHT = 52
const IMAGE_CARD_WIDTH = generationNodeCardWidth('generate_image')
const IMAGE_SKILL_KEY = 'generic_image_generation'
const IMAGE_SKILL_VERSION = 'v1'

// VistoeraTASK-00148 (§四/§十一) — `interrupted` (Worker Lease/heartbeat
// genuinely lost) must render its OWN distinct "任务中断，可重新生成" state,
// never the same spinner as a live `running` generation — previously
// conflated here (mirrors the same fix in
// `imageGenerationProvider.ts`'s `resolveCanvasImageTaskFieldStatus`,
// which this local helper must stay aligned with).
function taskStatusToFieldStatus(status: ImageGenerationTask['status']): string {
  if (status === 'succeeded') return 'COMPLETED'
  if (status === 'failed') return 'FAILED'
  if (status === 'interrupted') return 'INTERRUPTED'
  if (status === 'running') return 'GENERATING'
  return 'QUEUED'
}

// `interrupted` still counts as "in flight" for polling purposes: it is
// not a fully-terminal outcome in the shared Job model (an admin/backend
// recovery path may still resolve it), so Canvas keeps polling until a
// genuine terminal status (`succeeded`/`failed`) is observed — but the
// FIELD STATUS shown to the tenant while polling continues is now
// distinctly 'INTERRUPTED', not 'GENERATING' (see above).
function isImageTaskInFlight(status: ImageGenerationTask['status']): boolean {
  return status === 'queued' || status === 'running' || status === 'interrupted'
}

// VistoeraTASK-00148 (§十二) — once a terminal FAILED status has been
// observed for a given task_id, the frontend must never let a
// subsequently-fetched payload for the SAME task_id (a late/out-of-
// order poll response, a stale subscription event, etc.) silently
// resurrect it into a non-failed display. This is the defensive
// frontend guard layered on top of the backend's own atomic CAS
// terminal-transition protection (belt-and-suspenders, per spec §八/§十二).
const observedFailedTaskIds = new Set<string>()

function shouldRejectLateNonFailedUpdate(taskId: string | undefined, incomingStatus: ImageGenerationTask['status']): boolean {
  if (!taskId) return false
  if (incomingStatus === 'failed') {
    observedFailedTaskIds.add(taskId)
    return false
  }
  return observedFailedTaskIds.has(taskId)
}

function isImageTaskNotFound(err: unknown): boolean {
  const apiErr = err as SaasApiError
  return apiErr?.status === 404 || apiErr?.code === 'not_found'
}

function computePreviewBox(ratioChoice: string): { width: number; height: number } {
  const [wRaw, hRaw] = ratioForLayout(ratioChoice).split(':')
  const w = Number(wRaw) || 1
  const h = Number(hRaw) || 1
  return computeMediaBoxSize(Math.max(1, w) * 100, Math.max(1, h) * 100)
}

function GenerateImageNodeImpl({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as FlowNodeData & { workspaceId?: string; projectTitle?: string }
  const def = useNodeDefinition(nodeData.nodeType)
  const { nodeLabel } = useCanvasNodeRegistryI18n()
  const { t, qualityOptions, imageCountOptions, mediaHeader, regenerate } = useCanvasGenerationI18n()
  const displayLabel = nodeLabel(def.nodeType, def.label)
  const workspaceId = nodeData.workspaceId || ''
  // VistoeraTASK-00251 — same canonical, already-threaded-by-`CanvasBoard`
  // values `ImageAssetNode.tsx` uses to open its own "编辑图片" overlay
  // (see `types.ts`'s `siteId`/`projectId` doc comments) — no new prop
  // plumbing needed anywhere in `CanvasBoard.tsx`.
  const siteId = nodeData.siteId || null
  const projectId = nodeData.projectId
  const fields = nodeData.fields || {}
  const inputMaterials = nodeData.inputMaterials || []
  const imageMaterials = useMemo(() => inputMaterials.filter((m) => m.kind === 'IMAGE'), [inputMaterials])
  const providerChannel = (fields.providerChannel as string | undefined) ?? undefined
  const explicitProvider = Boolean(providerChannel)
  const imageGenConfig = nodeData.imageGenerationConfig
  useSyncExternalStore(subscribeAuthMatrix, getAuthMatrixRevision, getAuthMatrixRevision)
  const authMatrixStatus = getAuthMatrixStatus()
  const nodeAuthDenied = isNodeAuthDenied('generate_image')
  const providerChannels = useMemo(() => parseProviderChannels(imageGenConfig), [imageGenConfig])
  // VistoeraTASK-00691 — auth filter reads module cache; must recompute when
  // matrix status flips idle/loading → ready (deps were only providerChannels).
  const selectableProviderChannels = useMemo(
    () => authFilteredImageProviderChannels(providerChannels, 'generate_image'),
    [providerChannels, authMatrixStatus],
  )
  const selectedProvider = useMemo(
    () => resolveProviderChannel(fields, providerChannels, imageGenConfig),
    [fields, providerChannels, imageGenConfig],
  )
  const submitProvider = useMemo(
    () => resolveSubmitProviderChannel(fields, providerChannels, imageGenConfig),
    [fields, providerChannels, imageGenConfig],
  )
  const providerExecutionBlocked = Boolean(selectedProvider && !isImageProviderExecutionSupported(selectedProvider))
  const providerExecutionBlockMessage = imageProviderExecutionBlockMessage(
    selectedProvider,
    t('videoErrors.PROVIDER_EXECUTION_NOT_SUPPORTED'),
  )
  // VistoeraTASK-00253 §八 — a node whose SAVED `providerChannel` no
  // longer resolves against the live channel list (Admin disabled/
  // deleted/renamed-away-the-id the execution plan this node was
  // explicitly bound to) must show an explicit, visible status right
  // on the selector itself, never just fail silently at submit time
  // (the submit-time guard below already blocks the actual task
  // dispatch — this is the CASE 6 "explicit invalid state" affordance
  // that guard alone doesn't surface until the user clicks Generate).
  const providerChannelUnresolved = hasUnresolvedExplicitProviderChannel(fields, providerChannels)
  const showPromptTranslate = imageProviderRecommendsPromptTranslation(selectedProvider)
  const imageReferenceCapability = useMemo(
    () => resolveCapabilityForGenerateImageNode({ config: imageGenConfig, fields, workspaceId }),
    [fields, imageGenConfig, workspaceId],
  )
  const referenceLimitState = useMemo(
    () => evaluateImageReferenceLimit(countImageReferenceMaterials(inputMaterials), imageReferenceCapability),
    [imageReferenceCapability, inputMaterials],
  )
  const referenceLimitMessage = referenceLimitState.exceedsLimit
    ? referenceLimitValidationMessage(imageReferenceCapability, t)
    : null
  const relatedAssetsRemainingSlots = Math.max(
    0,
    referenceLimitState.maxReferenceImages - referenceLimitState.imageCount,
  )
  const openRelatedAssetsPicker = () => {
    // VistoeraTASK-00574 — 「相关资产 → + 添加」: open the shared Image
    // Asset Picker, then materialize image_asset nodes + REFERENCES edges
    // (board owns creation; this node never invents upload/edges itself).
    if (!siteId) {
      message.warning(t('mediaCard.selectImage'))
      return
    }
    if (relatedAssetsRemainingSlots <= 0) {
      message.warning(relatedAssetsAddLimitMessage(imageReferenceCapability, t))
      return
    }
    if (!nodeData.onOpenAssetPicker || !nodeData.onMaterializeAndConnectImageReferences) {
      return
    }
    nodeData.onOpenAssetPicker({
      nodeId: id,
      purpose: 'canvas_insert',
      title: t('generation.relatedAssets.addTitle'),
      selectionMode: 'multiple',
      maxSelection: relatedAssetsRemainingSlots,
      confirmLabel: (count) => t('generation.relatedAssets.confirmAdd', { count }),
      onConfirm: (selections) => {
        if (!selections.length) return
        if (selections.length > relatedAssetsRemainingSlots) {
          message.warning(relatedAssetsAddLimitMessage(imageReferenceCapability, t))
          return
        }
        nodeData.onMaterializeAndConnectImageReferences?.(id, selections)
        setRelatedAssetsOpen(true)
      },
    })
  }
  const promptConstraints = useMemo(
    () => resolvePromptConstraintsForGenerateImageNode({ config: imageGenConfig, fields }),
    [fields, imageGenConfig],
  )

  const prompt = (fields.prompt as string | undefined) ?? ''
  // VistoeraTASK-00498 — UI choice; free ratio (no saved ratio / "free") means
  // "no aspect-ratio constraint" and is NEVER sent as a value (see `ratioForSubmit`).
  // VistoeraTASK-00595 — trigger UI is ImageAspectRatioSelect; semantics unchanged.
  const ratio = resolveRatioChoice(fields.ratio)
  const quality = (fields.quality as string | undefined) ?? 'STANDARD'
  const candidateCount = (fields.candidateCount as number | undefined) ?? 1
  const strength = (fields.strength as number | undefined) ?? 0.75
  const status = (fields.status as string | undefined) ?? 'READY'
  const productionFormatLocked = Boolean(
    fields.productionFormatLocked || fields.lockedProductionFormat || fields.candidateForVideoKeyframe,
  )
  const lockedFormat = (fields.lockedProductionFormat as { aspect_ratio?: string; resolution?: string; version?: number } | null) ?? null
  const effectiveFormat =
    readProductionFormat({ productionFormat: lockedFormat }) || deriveProductionFormatFromVideoFields(fields)
  const formatSource = resolveKeyframeFormatSource(fields)
  const formatMismatch = (fields.formatCompatibility as string | undefined) === 'format_mismatch'
  const deliveryRenderStale = Boolean(fields.deliveryRenderStale) && !formatMismatch
  const productionTargetWidth =
    (fields.deliveryTargetWidth as number | null | undefined) ??
    (fields.productionTargetWidth as number | null | undefined)
  const productionTargetHeight =
    (fields.deliveryTargetHeight as number | null | undefined) ??
    (fields.productionTargetHeight as number | null | undefined)
  const lockedAspect = effectiveFormat?.aspect_ratio || ratio
  const lockedResolution = effectiveFormat?.resolution || null
  const canonicalTarget = effectiveFormat ? resolveCanonicalProductionTarget(effectiveFormat) : null
  const nativeSize = effectiveFormat ? resolveProviderNativeSizeForAspect(effectiveFormat.aspect_ratio) : null
  const formatSourceLabel =
    formatSource === 'storyboard' ? '来自：生成分镜' : formatSource === 'video_node' ? '来自：本视频节点' : null
  const generatedOutputId = fields.generatedOutputId as string | undefined
  const outputAssetId = (fields.outputAssetId as string | undefined) ?? null
  const previousOutputId = fields.previousOutputId as string | undefined
  const displayOutputId = resolveGenerationDisplayOutputId({ generatedOutputId, previousOutputId })
  const generatedFromFingerprint = fields.generatedFromFingerprint as string | undefined
  const generatedUpstreamFingerprint = fields.generatedUpstreamFingerprint as string | undefined
  const generatedConfigFingerprint = fields.generatedConfigFingerprint as string | undefined
  const hasResult = !!displayOutputId
  const skillSelectionSource = (fields.skillSelectionSource as string) || 'AUTO'

  const aliasByEdgeId = useMemo(() => computeMaterialAliases(inputMaterials, t), [inputMaterials, t])
  const thumbUrlByEdgeId = useMaterialThumbMap(workspaceId, inputMaterials)
  const { resolved: resolvedPrompt } = useMemo(() => resolvePromptForSubmit(prompt, aliasByEdgeId, t), [prompt, aliasByEdgeId, t])
  const promptLimitState = useMemo(
    () => evaluateImagePromptLimit(resolvedPrompt, promptConstraints),
    [promptConstraints, resolvedPrompt],
  )
  const promptLimitMessage = promptLimitState.exceedsLimit ? promptLimitValidationMessage(promptLimitState, t) : null
  const disconnectedMentionCount = useMemo(() => countDisconnectedMentions(prompt, aliasByEdgeId), [prompt, aliasByEdgeId])

  const connection = useConnection()
  const draggedSourceType: DataType | undefined = useMemo(() => {
    if (!connection.inProgress || connection.fromHandle?.type !== 'source') return undefined
    const sourceNodeType = (connection.fromNode?.data as unknown as FlowNodeData | undefined)?.nodeType
    if (!sourceNodeType) return undefined
    return nodeOutputDataTypes(sourceNodeType)[0]
  }, [connection])
  const acceptedTypes = getNodeIO('generate_image')?.acceptedInputTypes || []
  const atImageReferenceLimit =
    draggedSourceType === 'IMAGE' && referenceLimitState.imageCount >= referenceLimitState.maxReferenceImages
  const mediaDropState =
    draggedSourceType == null
      ? 'none'
      : atImageReferenceLimit
        ? 'reference-limit'
        : acceptedTypes.includes(draggedSourceType)
          ? 'candidate'
          : 'incompatible'

  const [task, setTask] = useState<ImageGenerationTask | null>(null)
  const [submitError, setSubmitError] = useState<{ code?: string; message: string } | null>(null)
  const [pollSyncError, setPollSyncError] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 })
  const [showMoreParams, setShowMoreParams] = useState(false)
  // VistoeraTASK-00144 (§十五 "no DB write on toggle") — Related Assets'
  // own open/closed state is PURELY a visual-projection toggle for edge
  // anchoring (spec §四), deliberately kept as local, non-persisted
  // `useState` (never `fields.relatedAssetsCollapsed`/autosave) unlike
  // Generate Video's `fields.referenceAssetsCollapsed` precedent — this
  // node's own performance budget explicitly forbids a persistence
  // write on a pure expand/collapse interaction.
  const [relatedAssetsOpen, setRelatedAssetsOpen] = useState(imageMaterials.length > 0)
  useEffect(() => {
    // Preserves `ImageRelatedAssetsSection`'s original P1 behavior
    // verbatim (force-open whenever a material newly appears) now that
    // the state lives one level up.
    if (inputMaterials.length > 0) setRelatedAssetsOpen(true)
  }, [inputMaterials.length])
  const [regenerateChoiceOpen, setRegenerateChoiceOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  // VistoeraTASK-00251 — Canvas Image Editor overlay, mirroring
  // `ImageAssetNode.tsx`'s own `editorOpen`/`editorInstanceKey` state
  // shape exactly (see that file's doc comments for the full rationale:
  // separate boolean from `isDetailExpanded`, `editorInstanceKey` only
  // ever bumped by the error boundary's own "重试"). NON_DESTRUCTIVE_EDIT
  // / SAVE_AS_NEW_ASSET — this generation result, its `outputAssetId`,
  // prompt, provider, parameters, related assets, and Generation History
  // are NEVER touched by opening, editing, cancelling, or saving here.
  const [editorOpen, setEditorOpen] = useState(false)
  // The canonical `SiteAsset` id backing the CURRENTLY DISPLAYED result
  // (`displayOutputId`/`outputAssetId` below) — resolved fresh every time
  // the editor is opened (never persisted onto this node's own fields,
  // see `handleEditImageClick`'s doc comment).
  const [editorSourceAssetId, setEditorSourceAssetId] = useState<string | null>(null)
  const [editorSourcePreparing, setEditorSourcePreparing] = useState(false)
  const [editorInstanceKey, setEditorInstanceKey] = useState(0)
  const editorDiagnosticsRef: EditorDiagnosticsRef = useMemo(
    () => ({ current: createEditorDiagnosticsSnapshot() }),
    [editorOpen, editorInstanceKey],
  )
  const [resultZoneActive, setResultZoneActive] = useState(false)
  const resultZoneLeaveTimerRef = useRef<number | null>(null)

  const activateResultZone = useCallback(() => {
    if (resultZoneLeaveTimerRef.current != null) {
      window.clearTimeout(resultZoneLeaveTimerRef.current)
      resultZoneLeaveTimerRef.current = null
    }
    setResultZoneActive(true)
  }, [])

  const deactivateResultZone = useCallback(() => {
    if (resultZoneLeaveTimerRef.current != null) {
      window.clearTimeout(resultZoneLeaveTimerRef.current)
    }
    resultZoneLeaveTimerRef.current = window.setTimeout(() => {
      setResultZoneActive(false)
      resultZoneLeaveTimerRef.current = null
    }, 140)
  }, [])

  const [runHydrating, setRunHydrating] = useState(() => Boolean(nodeData.latestRunId))
  const pollTimerRef = useRef<number | null>(null)
  const submitFingerprintRef = useRef<string | null>(null)
  const generationAttemptRef = useRef(0)
  // VistoeraTASK-00224 — tracks the most recent task_id THIS node has
  // already committed a fan-out for, so the accept-handler's decision
  // never depends on the (possibly one-render-stale) `fields` closure
  // snapshot of `imageGenerationBatchFanOutCommitted`. See
  // `resetImageGenerationBatchFieldsPatch`'s doc comment for the full
  // regression this guards against.
  const fanOutCommittedForTaskIdRef = useRef<string | null>(null)
  // VistoeraTASK-00242 — defense-in-depth twin of the ref above, for the
  // `applyCompletedTask` "excess-output" split fallback below. Even
  // though `CanvasBoard`'s `onSplitGenerationOutputs` now commits the
  // batch on the origin (so a stale/late-flushed `fieldsRef.current`
  // read can no longer let it run twice for the SAME origin+task
  // forever), this ref-keyed guard makes the "at most once per
  // (this node instance, task_id)" invariant hold SYNCHRONOUSLY and
  // independent of whether `nodesRef.current`/`fieldsRef.current` has
  // caught up yet — the exact kind of multi-layer idempotency the real
  // incident showed a single guard field is not sufficient for.
  const splitCommittedForTaskIdRef = useRef<string | null>(null)
  // VistoeraTASK-00512 — true once THIS mount observed the current run in flight.
  const taskSeenInFlightRef = useRef(false)
  // VistoeraTASK-00512 — group id of the Result Slots prepared for the run being submitted.
  const preparedBatchIdRef = useRef<string | null>(null)
  const autoGenerateConsumedRef = useRef(false)
  const runBtnRef = useRef<HTMLButtonElement | null>(null)
  const historyBtnRef = useRef<HTMLButtonElement | null>(null)
  const updateNodeInternals = useUpdateNodeInternals()
  // VistoeraTASK-00224 — `pollOnce`'s own recursive continuation and
  // `applyCompletedTask` are both `useCallback`s bound to the specific
  // `fields` snapshot from the render that created THAT closure
  // generation; once a poll chain starts, every one of its own
  // subsequent iterations keeps re-invoking the SAME closure generation
  // (self-recursion through the enclosing `const` binding), so it can
  // never observe a `patchFields` update applied from a LATER render —
  // including the very reset this task performs at submit-accept time.
  // `fieldsRef` is the standard escape hatch: always read the latest
  // fields for these specific batch-membership decisions instead of the
  // closure snapshot.
  const fieldsRef = useRef(fields)
  useEffect(() => {
    fieldsRef.current = fields
  }, [fields])

  const patchFields = useCallback(
    // VistoeraTASK-00228 — real Preview forensic (Parent task
    // 7021a2d7-4134-4d25-b0a6-52b51efd7e93, node n-aer4ef75-muhywxmq)
    // proved `fieldsRef` alone was NOT the full fix for the stale-closure
    // recursion trap TASK-00224 partially addressed: reading
    // `fieldsRef.current` inside `pollOnce`/`applyCompletedTask` fixed
    // their own DECISIONS, but `patchFields` itself still merged every
    // patch against the CLOSURE `fields` snapshot — frozen at whatever
    // render created the specific `patchFields` instance the frozen
    // `pollOnce` recursion chain is still holding onto, many renders (and
    // ~12 real minutes, in the forensic case) in the past.
    //
    // Concretely: the submit accept-handler's `patchFields(acceptedPatch)`
    // (the batch-fields reset) and `onCommitImageGenerationBatchFanOut`'s
    // OWN direct `commitNodes` write (setting
    // `imageGenerationBatchFanOutCommitted=true` + batchId/slotIndex on
    // THIS SAME node, moments later in the SAME accept-handler) both
    // landed correctly in `CanvasBoard`'s `nodesRef`/`nodes` state. But
    // ~12 minutes later, when the harness/task terminally failed,
    // `applyCompletedTask` — still running through the ORIGINAL frozen
    // closure captured at accept time — called this SAME OLD
    // `patchFields` instance, whose captured `fields` variable was still
    // the PRE-fan-out-commit snapshot. `patchCanvasFieldsIfChanged(fields,
    // patch)` merged the terminal patch onto THAT stale baseline and
    // shipped the result to `onChangeFields`, which replaces
    // `node.data.fields` WHOLESALE (see `CanvasBoard.onChangeFields`) —
    // silently reverting `imageGenerationBatchFanOutCommitted` back to
    // `false` and dropping the batchId/slotIndex/originNodeId fields
    // entirely. This is why the real user's candidate_count=2 submission
    // still only ever showed ONE Canvas node even running on top of the
    // TASK-00224 fix: the second node WAS created, but the origin node's
    // own batch-commit fields were silently clobbered by this terminal
    // write, and (per `onCommitImageGenerationBatchFanOut`'s own
    // `if (originFields[COMMITTED_FIELD]) return` idempotency guard) nothing
    // ever re-created or re-linked it after that.
    //
    // Fix: merge against `fieldsRef.current` (kept fresh by the `useEffect`
    // above on every render, regardless of which closure generation is
    // calling `patchFields`) instead of the closure `fields` — and mirror
    // every accepted patch back into `fieldsRef.current` synchronously so
    // a SECOND `patchFields` call made later in the SAME synchronous turn
    // (as the accept-handler already relies on) still sees it immediately,
    // without waiting for the next effect-driven sync from React re-render.
    (patch: Record<string, unknown>) => {
      const next = patchCanvasFieldsIfChanged(fieldsRef.current as Record<string, unknown>, patch)
      if (!next) return
      fieldsRef.current = next
      nodeData.onChangeFields?.(id, next)
    },
    [id, nodeData],
  )
  const submission = useCanvasGenerationSubmission({
    nodeId: id,
    fieldStatus: status,
    patchFields,
    runHydrating,
  })
  const { pinned, togglePin } = usePinnedExpandedNode(fields, patchFields)

  // VistoeraTASK-00306 — new nodes follow Platform Admin canonical default
  // Execution Plan once config loads; persisted explicit selections win.
  // VistoeraTASK-00634 — default must remain in the auth-allowed channel set.
  useEffect(() => {
    if (explicitProvider) return
    if (!imageGenConfig || selectableProviderChannels.length === 0) return
    const defaultChannelId = resolveDefaultProviderChannelId(imageGenConfig, selectableProviderChannels)
    const channelId =
      defaultChannelId && selectableProviderChannels.some((c) => c.id === defaultChannelId)
        ? defaultChannelId
        : selectableProviderChannels[0]?.id
    if (!channelId) return
    const channel = selectableProviderChannels.find((c) => c.id === channelId)
    patchFields({
      providerChannel: channelId,
      ...(channel?.model_id ? { modelId: channel.model_id } : {}),
    })
  }, [explicitProvider, imageGenConfig, selectableProviderChannels, patchFields])

  const providerAuthDenied = Boolean(
    selectedProvider?.provider_option_id &&
      !isChannelEffectiveAllowed('generate_image', selectedProvider.provider_option_id),
  )
  // Only treat as "no grants" once auth-matrix is READY — loading/error must
  // not reuse the same「暂无可用执行通道」copy (TASK-00691 / 00635).
  const noAllowedImageProviders =
    authMatrixStatus === 'ready' &&
    selectableProviderChannels.length === 0 &&
    providerChannels.length > 0

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current != null) {
      window.clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  const isBusy = submission.isBusy
  const hasReferences = imageMaterials.length > 0
  const operation = inferImageGenerationOperation(imageMaterials.length, providerChannel)

  const upstreamFingerprint = useMemo(() => computeImageUpstreamFingerprint(imageMaterials), [imageMaterials])

  const configFingerprint = useMemo(
    () =>
      computeImageConfigFingerprint({
        prompt: resolvedPrompt,
        ratio,
        quality,
        candidateCount,
        strength,
        operation,
      }),
    [candidateCount, operation, quality, ratio, resolvedPrompt, strength],
  )

  const inputFingerprint = useMemo(
    () =>
      computeGenerationInputFingerprint({
        referenceAssetIds: legacyImageReferenceAssetIds(imageMaterials),
        prompt: resolvedPrompt,
        ratio,
        quality,
        candidateCount,
        strength,
        operation,
      }),
    [candidateCount, imageMaterials, operation, quality, ratio, resolvedPrompt, strength],
  )

  const driftState = useMemo(
    () =>
      computeGenerationDriftState({
        hasResult,
        needsRegenerationFlag: Boolean(fields.needsRegeneration),
        generatedUpstreamFingerprint,
        generatedConfigFingerprint,
        generatedFromFingerprint,
        currentUpstreamFingerprint: upstreamFingerprint,
        currentConfigFingerprint: configFingerprint,
        legacyReferenceAssetIds: legacyImageReferenceAssetIds(imageMaterials),
        legacyConfigSnapshot: {
          prompt: resolvedPrompt,
          ratio,
          quality,
          candidateCount,
          strength,
          operation,
        },
      }),
    [
      candidateCount,
      configFingerprint,
      fields.needsRegeneration,
      generatedConfigFingerprint,
      generatedFromFingerprint,
      generatedUpstreamFingerprint,
      hasResult,
      imageMaterials,
      operation,
      quality,
      ratio,
      resolvedPrompt,
      strength,
      upstreamFingerprint,
    ],
  )

  const driftInspectorText = generationDriftInspectorText(driftState, t)

  const handleOrphanedRun = useCallback(() => {
    stopPolling()
    setTask(null)
    nodeData.onChangeLatestRunId?.(id, null)
    patchFields({ status: hasResult ? 'COMPLETED' : 'READY', needsRegeneration: false })
    setSubmitError({ message: t('generation.errorPreviousIncomplete') })
    submitFingerprintRef.current = null
  }, [hasResult, id, nodeData, patchFields, stopPolling, t])

  // VistoeraTASK-00143 P6/P7 — a hydrated `latestRunId` whose Run/Task was
  // submitted under a DIFFERENT provider channel than the node's current
  // selection is HISTORY, not the current execution state (see
  // `isImageGenerationRunOwnedByCurrentProvider`). This can only be reached
  // if a provider switch's persisted reset never reached the backend before
  // a refresh (race/offline/crash) — the switch-time reset itself already
  // clears `latestRunId` synchronously in the common case. Silently (no
  // user-facing error — nothing failed on the CURRENT provider) reset to a
  // clean idle state and persist the clear so it cannot resurface again.
  const handleStaleProviderRun = useCallback(() => {
    stopPolling()
    setTask(null)
    nodeData.onChangeLatestRunId?.(id, null)
    patchFields({ status: hasResult ? 'COMPLETED' : 'READY', needsRegeneration: false })
    submitFingerprintRef.current = null
  }, [hasResult, id, nodeData, patchFields, stopPolling])

  // VistoeraTASK-00512 — Result Slot recovery (append slots for outputs that
  // have no Canvas carrier) is only allowed for a run this node OBSERVED in
  // flight (submit / poll / hydrated while still running), and only from the
  // group's leader, so a slot the user deleted AFTER completion is never
  // resurrected by a refresh. Refs only — safe to call from any closure.
  const slotRecoveryOpts = () => ({
    allowSlotRecovery:
      taskSeenInFlightRef.current && !isImageGenerationBatchFollower(fieldsRef.current as Record<string, unknown>),
  })

  const applyCompletedTask = useCallback(
    (next: ImageGenerationTask, capturedAttemptId?: number) => {
      if (
        capturedAttemptId != null &&
        shouldDiscardImageGenerationAttempt(capturedAttemptId, generationAttemptRef.current)
      ) {
        return
      }
      // VistoeraTASK-00148 (§十二) — defensive frontend guard: once THIS
      // task_id has been observed as `failed`, no later-arriving payload
      // for the SAME task_id (out-of-order poll response, duplicate
      // in-flight request, stale subscription replay) may ever flip the
      // display away from the failed state again — even a `succeeded`
      // one. The backend's own atomic CAS terminal-transition guard
      // (`JobService.mark_succeeded`/`mark_failed`) is the first line of
      // defense; this is the required second one.
      if (shouldRejectLateNonFailedUpdate(next.id, next.status)) {
        return
      }
      setTask(next)
      if (next.status !== 'succeeded' || next.outputs.length === 0) {
        // VistoeraTASK-00224 — read the batch-membership fields from
        // `fieldsRef` (always current), not the closure `fields` this
        // callback was bound to, so a batch reset applied by a LATER
        // render (e.g. this node's own accept-handler, moments earlier
        // in the same submission) is honored rather than the stale
        // pre-reset snapshot.
        const currentBatchFields = fieldsRef.current
        const terminalPatch = currentBatchFields[IMAGE_GENERATION_BATCH_COMMITTED_FIELD]
          ? buildImageGenerationSlotTaskFieldsPatch(
              next,
              imageGenerationBatchSlotIndex(currentBatchFields),
              requestedImageGenerationBatchCount(currentBatchFields),
              resolveCanvasImageTaskFieldStatus,
            )
          : null
        patchFields(
          terminalPatch ?? {
            status: taskStatusToFieldStatus(next.status),
            previousOutputId: undefined,
          },
        )
        submitFingerprintRef.current = null
        if (next.status === 'failed') {
          submission.cancelSubmission()
          const failure = localizeImageGenerationTaskError(
            { errorCode: next.error_code, errorMessage: next.error_message, taskId: next.id },
            t,
          )
          setSubmitError({
            code: failure.code ?? undefined,
            message: failure.taskId
              ? `${failure.message} (${t('generation.errors.taskIdHint', { taskId: failure.taskId })})`
              : failure.message,
          })
        }
        if (currentBatchFields[IMAGE_GENERATION_BATCH_COMMITTED_FIELD]) {
          nodeData.onSyncImageGenerationBatchFromTask?.(id, next, slotRecoveryOpts())
        }
        return
      }
      setSubmitError(null)
      setPollSyncError(false)
      const capturedFingerprint = submitFingerprintRef.current || inputFingerprint
      const fingerprintDrift =
        Boolean(submitFingerprintRef.current) && submitFingerprintRef.current !== inputFingerprint
      submitFingerprintRef.current = null
      // See the comment above — same stale-closure hazard applies here.
      const slotIndex = imageGenerationBatchSlotIndex(fieldsRef.current)
      const slotOutput = pickImageGenerationOutputForSlot(next, slotIndex)
      if (!slotOutput) {
        patchFields({
          status: 'FAILED',
          previousOutputId: undefined,
        })
        nodeData.onSyncImageGenerationBatchFromTask?.(id, next, slotRecoveryOpts())
        return
      }
      patchFields({
        status: 'COMPLETED',
        generatedOutputId: slotOutput.id,
        outputAssetId: slotOutput.site_asset_id || null,
        previousOutputId: undefined,
        generatedUpstreamFingerprint: upstreamFingerprint,
        generatedConfigFingerprint: configFingerprint,
        generatedFromFingerprint: capturedFingerprint,
        needsRegeneration: fingerprintDrift,
        formatCompatibility: 'ok',
        generatedAgainstFormatVersion:
          typeof fields.generatedAgainstFormatVersion === 'number'
            ? fields.generatedAgainstFormatVersion
            : typeof lockedFormat?.version === 'number'
              ? lockedFormat.version
              : null,
        production_format_version:
          typeof lockedFormat?.version === 'number' ? lockedFormat.version : fields.production_format_version ?? null,
        aspect_ratio: lockedFormat?.aspect_ratio || ratioForSubmit(ratio) || null,
        target_resolution: lockedFormat?.resolution || null,
        productionTargetWidth: productionTargetWidth ?? null,
        productionTargetHeight: productionTargetHeight ?? null,
        provider_native_width: slotOutput.width ?? fields.provider_native_width ?? null,
        provider_native_height: slotOutput.height ?? fields.provider_native_height ?? null,
      })
      if (fieldsRef.current[IMAGE_GENERATION_BATCH_COMMITTED_FIELD]) {
        nodeData.onSyncImageGenerationBatchFromTask?.(id, next, slotRecoveryOpts())
      } else if (splitCommittedForTaskIdRef.current !== next.id) {
        // VistoeraTASK-00242 — set the ref-keyed guard BEFORE calling
        // out, synchronously, so a second `applyCompletedTask(next)`
        // invocation for this SAME task_id (re-poll, mount-hydration
        // effect re-fire, StrictMode double-invoke) that races ahead of
        // `CanvasBoard`'s own commit-then-rerender round trip still
        // can't slip through and re-split the same excess outputs again.
        splitCommittedForTaskIdRef.current = next.id
        const rest = next.outputs.filter((o) => o.id !== slotOutput.id)
        if (rest.length > 0) {
          nodeData.onSplitGenerationOutputs?.(id, rest)
        }
      }
    },
    [
      hasResult,
      id,
      inputFingerprint,
      nodeData,
      patchFields,
      upstreamFingerprint,
      configFingerprint,
      fields.generatedAgainstFormatVersion,
      fields.production_format_version,
      fields.provider_native_width,
      fields.provider_native_height,
      lockedFormat,
      ratio,
      productionTargetWidth,
      productionTargetHeight,
      submission,
      t,
    ],
  )

  const pollOnce = useCallback(
    (taskId: string) => {
      // VistoeraTASK-00224 — read via `fieldsRef` (always current), not
      // the closure `fields` this `useCallback` generation was bound
      // to. `pollOnce` recurses through its OWN enclosing `const`
      // binding on every subsequent iteration, so a closure-captured
      // `fields` snapshot would otherwise stay frozen at whatever it
      // was when THIS poll chain started — even across a batch-fields
      // reset applied by a later render (e.g. this same node resubmitting
      // a brand-new generation after previously being a fan-out slot).
      if (!isImageGenerationBatchPollLeader(fieldsRef.current)) return
      getImageGenerationTask(workspaceId, taskId)
        .then(({ data: next }) => {
          setPollSyncError(false)
          // VistoeraTASK-00148 (§十二) — a late/out-of-order in-flight-
          // looking snapshot for a task_id already observed `failed`
          // must not regress the display back to a spinner either.
          if (shouldRejectLateNonFailedUpdate(next.id, next.status)) {
            return
          }
          if (isImageTaskInFlight(next.status)) {
            taskSeenInFlightRef.current = true
            setTask(next)
            patchFields({ status: resolveCanvasImageTaskFieldStatus(next) })
            if (fieldsRef.current[IMAGE_GENERATION_BATCH_COMMITTED_FIELD]) {
              nodeData.onSyncImageGenerationBatchFromTask?.(id, next, slotRecoveryOpts())
            }
            pollTimerRef.current = window.setTimeout(() => pollOnce(taskId), POLL_INTERVAL_MS)
          } else {
            applyCompletedTask(next)
          }
        })
        .catch((err: unknown) => {
          if (isImageTaskNotFound(err)) {
            handleOrphanedRun()
            return
          }
          setPollSyncError(true)
          pollTimerRef.current = window.setTimeout(() => pollOnce(taskId), POLL_INTERVAL_MS)
        })
    },
    [applyCompletedTask, handleOrphanedRun, id, nodeData, patchFields, workspaceId],
  )

  useEffect(() => {
    stopPolling()
    setTask(null)
    if (!nodeData.latestRunId || !workspaceId) {
      setRunHydrating(false)
      return
    }
    setRunHydrating(true)
    // VistoeraTASK-00143 P6/P7 ownership snapshot — captured once per mount
    // effect run (NOT re-read after async resolution) so it reflects the
    // provider that was current when hydration STARTED, matching the
    // provider the persisted `lastSubmittedGenerationParameters` snapshot
    // (if any) would have been compared against at switch-time.
    const submittedProviderChannel =
      (fields.lastSubmittedGenerationParameters as { providerChannel?: string | null } | undefined)
        ?.providerChannel ?? null
    const runOwnedByCurrentProvider = isImageGenerationRunOwnedByCurrentProvider({
      submittedProviderChannel,
      currentProviderChannel: providerChannel ?? null,
    })
    const batchFollower = !isImageGenerationBatchPollLeader(fields)
    // VistoeraTASK-00512 — the persisted status said "still running" when this
    // mount started: the run is observed in flight across the refresh.
    taskSeenInFlightRef.current = isImageGenerationSlotStatusRunning(fieldsRef.current.status)
    getImageGenerationTask(workspaceId, nodeData.latestRunId)
      .then(({ data: next }) => {
        setPollSyncError(false)
        if (!runOwnedByCurrentProvider) {
          handleStaleProviderRun()
          return
        }
        // VistoeraTASK-00148 (§十二) — same late-update guard for the
        // mount-hydration path (a fresh Canvas mount re-fetching this
        // task_id must not resurrect a display state we already retired).
        if (shouldRejectLateNonFailedUpdate(next.id, next.status)) {
          return
        }
        const slotPatch = buildImageGenerationSlotTaskFieldsPatch(
          next,
          imageGenerationBatchSlotIndex(fields),
          requestedImageGenerationBatchCount(fields),
          resolveCanvasImageTaskFieldStatus,
        )
        if (slotPatch) patchFields(slotPatch)
        if (isImageTaskInFlight(next.status)) {
          taskSeenInFlightRef.current = true
          setTask(next)
          if (!batchFollower) {
            pollTimerRef.current = window.setTimeout(() => pollOnce(next.id), POLL_INTERVAL_MS)
          }
        } else {
          applyCompletedTask(next)
        }
      })
      .catch((err: unknown) => {
        if (isImageTaskNotFound(err)) {
          handleOrphanedRun()
          return
        }
        setPollSyncError(true)
        if (!batchFollower) {
          pollTimerRef.current = window.setTimeout(() => pollOnce(nodeData.latestRunId!), POLL_INTERVAL_MS)
        }
      })
      .finally(() => setRunHydrating(false))
    return () => stopPolling()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeData.latestRunId, workspaceId])

  useEffect(() => stopPolling, [stopPolling])

  useEffect(
    () => () => {
      if (resultZoneLeaveTimerRef.current != null) {
        window.clearTimeout(resultZoneLeaveTimerRef.current)
      }
    },
    [],
  )

  const handleProviderChange = useCallback(
    (channelId: string) => {
      generationAttemptRef.current = nextImageGenerationAttemptId(generationAttemptRef.current)
      stopPolling()
      setSubmitError(null)
      setPollSyncError(false)
      setTask(null)
      submitFingerprintRef.current = null
      submission.cancelSubmission()
      const shouldClearFailedRun = shouldClearImageGenerationFailedRunIdentity({
        currentStatus: status,
        taskFailed: task?.status === 'failed',
      })
      if (shouldClearFailedRun) {
        nodeData.onChangeLatestRunId?.(id, null)
      }
      const resetPatch = buildImageProviderSwitchExecutionResetPatch({
        hasPersistedResult: hasResult,
        currentStatus: status,
      })
      patchFields({
        ...buildImageProviderSelectionPatch(channelId, providerChannels),
        ...(resetPatch ?? {}),
      })
    },
    [
      hasResult,
      id,
      nodeData,
      patchFields,
      providerChannels,
      status,
      stopPolling,
      submission,
      task?.status,
    ],
  )

  const runTask = useCallback(() => {
    if (referenceLimitState.exceedsLimit) {
      setSubmitError({ message: referenceLimitValidationMessage(imageReferenceCapability, t) })
      return
    }
    if (promptLimitState.exceedsLimit) {
      setSubmitError({ message: promptLimitValidationMessage(promptLimitState, t) })
      return
    }
    if (disconnectedMentionCount > 0) {
      setSubmitError({ message: t('generation.errorDisconnectedMentions') })
      return
    }
    if (providerChannel && providerChannel !== PROVIDER_CHANNEL_LEGACY && !submitProvider) {
      setSubmitError({ message: t('generation.errorProviderChannelUnavailable') })
      return
    }
    if (hasReferences && imageMaterials.some((m) => !m.ready)) {
      setSubmitError({ message: t('generation.errorReferenceNotReady') })
      patchFields({ status: hasResult ? 'COMPLETED' : 'READY' })
      return
    }
    const submitRatio = productionFormatLocked ? lockedAspect : ratio
    const submitQuality = productionFormatLocked
      ? lockedResolution === '1080p'
        ? 'HIGH'
        : lockedResolution === '720p'
          ? 'STANDARD'
          : quality
      : quality
    if (productionFormatLocked && effectiveFormat) {
      const gate = assertProviderSupportsProductionAspect({
        aspectRatio: submitRatio,
        supportedRatios: selectedProvider?.supported_ratios,
        parameterCapabilities: selectedProvider?.parameter_capabilities as
          | Record<string, { supported?: boolean; supported_values?: string[] }>
          | null
          | undefined,
      })
      if (!gate.ok) {
        setSubmitError({ message: gate.message })
        return
      }
    }
    // VistoeraTASK-00512 — BEFORE the run exists, guarantee exactly
    // `candidateCount` Result Slots (reuse this group's slots, recreate the
    // ones the user deleted while idle, detach extras). Every slot then enters
    // "generating" together with the run.
    const preparedSlots = nodeData.onPrepareImageGenerationResultSlots?.(id, { requestedCount: candidateCount })
    if (preparedSlots && !preparedSlots.ok) {
      setSubmitError({ message: t('generation.errorSlotsStillRunning') })
      return
    }
    if (preparedSlots?.originPatch) patchFields(preparedSlots.originPatch)
    setSubmitError(null)
    setPollSyncError(false)
    setTask(null)
    if (!submission.beginSubmission()) {
      if (preparedSlots?.batchId) nodeData.onSettleImageGenerationBatchSubmitFailure?.(id, null)
      setSubmitError({ message: t('generation.errorSubmitInProgress') })
      return
    }
    preparedBatchIdRef.current = preparedSlots?.batchId ?? null
    taskSeenInFlightRef.current = true
    const attemptId = nextImageGenerationAttemptId(generationAttemptRef.current)
    generationAttemptRef.current = attemptId
    Promise.all(
      imageMaterials.map((m) =>
        m.entityId
          ? Promise.resolve(m.entityId)
          : m.generatedOutputId
            ? materializeImageGenerationOutput(workspaceId, m.generatedOutputId).then(({ data }) => data.asset_id)
            : Promise.reject(new Error(t('generation.errorMissingDataSource'))),
      ),
    )
      .then((assetIds) => {
        const materialsWithIds = imageMaterials.map((m, idx) => ({ ...m, entityId: assetIds[idx] }))
        const { assetRefByEdgeId } = readReferenceBindingsFromFields(fields)
        const bindings = buildReferenceBindings({
          imageMaterials: materialsWithIds,
          assetRefByEdgeId,
          prompt,
          aliasByEdgeId,
          t,
        })
        const referenceImageInputs = bindings.referenceImageInputs.map((r, idx) => ({
          ...r,
          asset_id: assetIds[idx],
        }))
        patchFields({
          referenceImageInputs,
          promptAssetBindings: bindings.promptAssetBindings,
          referenceAssetRefByEdgeId: serializeReferenceAssetRefByEdgeId(assetRefByEdgeId),
        })
        const candidate = fields.candidateForVideoKeyframe as
          | { videoNodeId?: string; timelineEntryId?: string }
          | null
          | undefined
        const expectedReferenceAssetIds = (
          (fields.expectedReferenceAssetIds as string[] | undefined) || []
        ).filter(Boolean)
        const sourceContext =
          productionFormatLocked && effectiveFormat
            ? buildKeyframeImageSourceContext({
                nodeId: id,
                format: effectiveFormat,
                formatSource,
                videoNodeId: (fields.boundVideoNodeId as string | undefined) || candidate?.videoNodeId || null,
                timelineEntryId:
                  (fields.boundKeyframeEntryId as string | undefined) || candidate?.timelineEntryId || null,
                orchestratorNodeId: (fields.boundOrchestratorNodeId as string | undefined) || null,
                expectedReferenceAssetIds,
              })
            : { source: 'production_canvas', node_id: id }
        const submittedGenerationParameters = {
          prompt: resolvedPrompt.trim(),
          ratio: submitRatio,
          quality: submitQuality,
          candidateCount,
          providerChannel: providerChannel ?? null,
          strength: hasReferences ? strength : undefined,
          operation,
          productionFormat: effectiveFormat,
          purpose: (sourceContext as { purpose?: string }).purpose || 'generic',
        }
        patchFields({ lastSubmittedGenerationParameters: submittedGenerationParameters })
        submitFingerprintRef.current = computeGenerationInputFingerprint({
          referenceAssetIds: assetIds,
          prompt: resolvedPrompt.trim(),
          ratio: submitRatio,
          quality: submitQuality,
          candidateCount,
          strength,
          operation,
        })
        const submitPayload = buildImageGenerationSubmitPayload({
          channel: submitProvider,
          prompt: resolvedPrompt.trim(),
          operation,
          ratio: submitRatio,
          quality: submitQuality,
          candidateCount,
          strength: hasReferences ? strength : undefined,
          referenceImageInputs,
          promptAssetBindings: bindings.promptAssetBindings,
          sourceContext,
        })
        submission.markRequestStart()
        return createImageGenerationTask(workspaceId, submitPayload)
      })
      .then(({ data: created }) => {
        if (shouldDiscardImageGenerationAttempt(attemptId, generationAttemptRef.current)) {
          return
        }
        nodeData.onChangeLatestRunId?.(id, created.id)
        setTask(created)
        // VistoeraTASK-00224 — every accepted task represents THIS node
        // starting an entirely new, independent generation attempt.
        // Any batch-membership fields left over from a PAST, unrelated
        // batch (this node was a fan-out leader or follower before —
        // e.g. the user clicked "重新生成"/Generate again on a node
        // that already carries `imageGenerationBatchFanOutCommitted`)
        // must be cleared as part of THIS SAME patch, not a separate
        // one, so every reader that inspects `fields` after this point
        // (this handler's own fan-out/poll decisions below, the fan-out
        // committer in `CanvasBoard.tsx` reading `nodesRef.current`,
        // and `fieldsRef` via the sync effect above) observes a clean
        // slate rather than the stale prior batch identity. See
        // `resetImageGenerationBatchFieldsPatch`'s doc comment for the
        // full regression (missing second Canvas node + permanently
        // stuck "GENERATING" sibling) this prevents.
        // VistoeraTASK-00512 — when the Result Slots were prepared BEFORE
        // submit, this node already carries the NEW group identity: do NOT
        // reset it (that would orphan the prepared slots).
        const slotsPrepared = preparedBatchIdRef.current != null
        preparedBatchIdRef.current = null
        const acceptedPatch: Record<string, unknown> = {
          ...(slotsPrepared ? {} : resetImageGenerationBatchFieldsPatch()),
          status: resolveCanvasImageTaskFieldStatus(created),
        }
        if (generatedOutputId) {
          acceptedPatch.previousOutputId = generatedOutputId
        }
        patchFields(acceptedPatch)
        // Keep `fieldsRef` correct for the REST of this synchronous
        // handler (and for `applyCompletedTask`/`pollOnce` invocations
        // that may run before the sync effect above has a chance to
        // fire) — belt-and-suspenders alongside that effect.
        fieldsRef.current = { ...fieldsRef.current, ...acceptedPatch }
        submission.markJobAccepted()
        // Ref-keyed (not `fields`-keyed) idempotency: this node just
        // itself accepted `created.id`, so it unconditionally owns the
        // fan-out decision for THIS specific task — never skipped
        // because of a stale flag inherited from a past task.
        if (slotsPrepared) {
          fanOutCommittedForTaskIdRef.current = created.id
          nodeData.onBindImageGenerationBatchRun?.(id, created.id)
        } else if (candidateCount > 1 && fanOutCommittedForTaskIdRef.current !== created.id) {
          fanOutCommittedForTaskIdRef.current = created.id
          nodeData.onCommitImageGenerationBatchFanOut?.(id, {
            taskId: created.id,
            requestedCount: candidateCount,
            fieldStatus: resolveCanvasImageTaskFieldStatus(created),
          })
        }
        if (created.status === 'queued' || created.status === 'running' || created.status === 'interrupted') {
          // This node itself just submitted `created` — it is
          // unconditionally the poll owner of THIS task_id, regardless
          // of any (now-reset) historical batch-follower state. A
          // follower never calls `createImageGenerationTask` itself;
          // whichever node DOES call it always owns polling its result.
          pollTimerRef.current = window.setTimeout(() => pollOnce(created.id), POLL_INTERVAL_MS)
        } else {
          applyCompletedTask(created, attemptId)
        }
      })
      .catch((err: unknown) => {
        if (shouldDiscardImageGenerationAttempt(attemptId, generationAttemptRef.current)) {
          return
        }
        submitFingerprintRef.current = null
        const apiErr = err as SaasApiError
        const slotsPreparedForFailedSubmit = preparedBatchIdRef.current != null
        preparedBatchIdRef.current = null
        const presentation = localizeImageGenerationSubmitApiError(
          { code: apiErr.code, message: apiErr.message, status: apiErr.status },
          t,
        )
        const terminalFailed = presentation.category !== 'NETWORK_UNREACHABLE'
        submission.failSubmission({
          status: terminalFailed ? 'FAILED' : hasResult ? 'COMPLETED' : 'READY',
        })
        setSubmitError({
          code: presentation.code ?? undefined,
          message: presentation.message,
        })
        if (slotsPreparedForFailedSubmit) {
          nodeData.onSettleImageGenerationBatchSubmitFailure?.(id, presentation.message)
        }
      })
  }, [
    applyCompletedTask,
    candidateCount,
    disconnectedMentionCount,
    effectiveFormat,
    formatSource,
    generatedOutputId,
    hasReferences,
    hasResult,
    id,
    imageMaterials,
    imageReferenceCapability,
    inputFingerprint,
    lockedAspect,
    lockedResolution,
    nodeData,
    operation,
    patchFields,
    pollOnce,
    productionFormatLocked,
    prompt,
    providerChannel,
    quality,
    ratio,
    promptLimitState,
    referenceLimitState.exceedsLimit,
    resolvedPrompt,
    selectedProvider,
    submitProvider,
    providerChannels,
    strength,
    t,
    workspaceId,
    aliasByEdgeId,
    fields,
    submission,
  ])

  useEffect(() => {
    if (!fields.pendingAutoGenerate || isBusy || autoGenerateConsumedRef.current) return
    autoGenerateConsumedRef.current = true
    patchFields({ pendingAutoGenerate: false })
    if (!resolvedPrompt.trim()) return
    runTask()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.pendingAutoGenerate, isBusy])

  const handleGenerateClick = useCallback(() => {
    if (isBusy || submission.submitLockRef.current) {
      message.info(t('generation.errorSubmitInProgress'))
      return
    }
    if (referenceLimitState.exceedsLimit) {
      setSubmitError({ message: referenceLimitValidationMessage(imageReferenceCapability, t) })
      return
    }
    if (promptLimitState.exceedsLimit) {
      setSubmitError({ message: promptLimitValidationMessage(promptLimitState, t) })
      return
    }
    if (!resolvedPrompt.trim()) {
      message.warning(t('generation.promptRequiredImage'))
      return
    }
    if (hasResult) {
      setRegenerateChoiceOpen(true)
      return
    }
    runTask()
  }, [hasResult, imageReferenceCapability, isBusy, promptLimitState, referenceLimitState.exceedsLimit, resolvedPrompt, runTask, submission.submitLockRef, t])

  const handleOverwriteRegenerate = useCallback(() => {
    setRegenerateChoiceOpen(false)
    runTask()
  }, [runTask])

  const handleRegenerateSeparately = useCallback(() => {
    setRegenerateChoiceOpen(false)
    nodeData.onDuplicateForRegenerate?.(id)
  }, [id, nodeData])

  const handleDownload = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation()
      if (!displayOutputId) return
      const url = resolveImageGenMasterUrl(workspaceId, displayOutputId, outputAssetId)
      if (url) downloadCanvasMedia(url)
    },
    [displayOutputId, outputAssetId, workspaceId],
  )

  // VistoeraTASK-00251 — NON_DESTRUCTIVE_EDIT / SAVE_AS_NEW_ASSET.
  // Resolves the canonical `SiteAsset` id backing the CURRENTLY DISPLAYED
  // result (never the latest/origin/candidate-0 output — `displayOutputId`/
  // `outputAssetId` are the exact same pair the result `<img>` itself
  // renders from, see `resultUrl`/`hasSuccessImage` below) and opens it in
  // the editor as its `sourceAssetId`. §6/§20/§21 — a generation output
  // may not have been materialized into a `SiteAsset` yet (still served
  // directly from `ImageGenerationOutputAsset` bytes); rather than
  // downloading it to the browser and re-uploading a temporary copy just
  // to open the editor, this calls the SAME canonical, idempotent
  // backend materialize bridge `runTask` already uses for reference
  // images (`materializeImageGenerationOutput` — repeat calls for the
  // same output_id always return the SAME `SiteAsset`, never create a
  // duplicate). The resolved id is held only in local component state —
  // it is NEVER written back onto this node's own `fields` (no
  // `patchFields` call anywhere in this handler), so `outputAssetId` and
  // every other generation field stay byte-for-byte unchanged regardless
  // of how many times "编辑图片" is opened.
  const handleEditImageClick = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation()
      if (!workspaceId || !siteId) return
      const resolution = resolveGenerateImageEditSource({ displayOutputId, outputAssetId })
      if (resolution.mode === 'unavailable') return
      if (resolution.mode === 'asset') {
        setEditorSourceAssetId(resolution.assetId)
        setEditorOpen(true)
        return
      }
      setEditorSourcePreparing(true)
      materializeImageGenerationOutput(workspaceId, resolution.outputId)
        .then(({ data }) => {
          setEditorSourceAssetId(data.asset_id)
          setEditorOpen(true)
        })
        .catch(() => {
          message.error(t('generation.editImagePrepareFailed'))
        })
        .finally(() => setEditorSourcePreparing(false))
    },
    [displayOutputId, outputAssetId, siteId, t, workspaceId],
  )

  // §26/§27/§28/§29 (mirrors `ImageAssetNode.tsx`'s own
  // `handleEditorSavedAsNewAsset` doc comment) — the overlay itself has
  // already created the brand-new `SiteAsset` (a real, independent
  // library asset, `NEW_ASSET_ID != SOURCE_ASSET_ID`) before calling this.
  //
  // VistoeraTASK-00440 §12 — PREVIOUSLY (TASK-00251 §14/§15) this node
  // type deliberately did NOT ask `CanvasBoard` to place a new sibling
  // Canvas node here, so a "编辑图片" save on a generation RESULT only
  // ever reached the Image Asset library, never the Canvas itself — the
  // admin's own words: "我们所有的图片编辑器，在修改图片点击保存之后，
  // 都应该自动把修改后的图片当成图片资产放到画布上". That product decision
  // is explicitly SUPERSEDED by this task: this is now the exact same
  // pass-through `ImageAssetNode.tsx` already uses — `CanvasBoard`'s
  // `onSaveEditedImageAsNewAsset(nodeId, newAssetId)` creates ONE new
  // sibling `image_asset` node (never mutating THIS node/its own
  // `outputAssetId`), placed beside this node via the same
  // `placeNodeBesideSource` helper every other derived-node flow in this
  // file already uses (`onQuickGenerate`, `onDuplicateNode`, etc.) — no
  // new node-creation path, no new placement algorithm. `nodeData.
  // onSaveEditedImageAsNewAsset` is only ever actually wired for
  // `generate_image`/`image_asset` nodes (see `CanvasBoard.tsx`'s own
  // node-props wiring) — the `?? false` fallback is defensive only, the
  // same shape `ImageAssetNode.tsx` uses.
  const handleEditorSavedAsNewAsset = useCallback(
    async (newAssetId: string): Promise<boolean> => {
      const ok = await nodeData.onSaveEditedImageAsNewAsset?.(id, newAssetId)
      return ok ?? false
    },
    [id, nodeData],
  )

  const handleEditorRetry = useCallback(() => {
    setEditorInstanceKey((k) => k + 1)
  }, [])

  const handleCopyClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onDuplicateNode?.(id)
  }
  // VistoeraTASK-00512 — a Result Slot of a RUNNING multi-output run cannot be
  // deleted (the Board's delete handlers enforce the same rule for keyboard /
  // bulk / context-menu paths; this only reflects it in the UI).
  const deleteLockedByRun = isImageGenerationResultSlotRunning(fields)
  const handleDeleteClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (deleteLockedByRun) return
    nodeData.onRequestDelete?.(id, e.currentTarget)
  }
  const { onClick: onMediaSingleClick, onDoubleClick: onMediaDoubleClick } = useSingleOrDoubleClick(
    () => {
      nodeData.onExpandFunctionCardDetail?.(id)
    },
    () => setPreviewOpen(true),
  )

  const isDetailExpanded = Boolean(nodeData.isDetailExpanded)
  const showExpanded = functionCardShowsInspector(isDetailExpanded, nodeData.isDragTempCollapsed)

  const resultUrl = resolveImageGenCanvasDisplayUrl(workspaceId, displayOutputId, outputAssetId)
  const masterUrl = resolveImageGenMasterUrl(workspaceId, displayOutputId, outputAssetId)
  const showActivePreview = shouldShowActiveGenerationOutput(displayOutputId, status, {
    hasExistingResult: !!generatedOutputId,
    runHydrating,
    latestRunId: nodeData.latestRunId,
  })

  const expandedWidth = IMAGE_CARD_WIDTH
  const hasSuccessImage = showActivePreview && Boolean(resultUrl)

  // VistoeraTASK-00147 — same canonical viewport-gate + progressive
  // thumbnail→preview primitive `ImageAssetNode` uses (see that file's
  // wiring + `useCanvasProgressiveImage.ts`'s doc comment for the real
  // forensic root-cause numbers). GENERATION status (`status`/`phase`
  // above) and MEDIA status (`mediaImage.isLoading`/`hasError` below) are
  // two fully independent state machines — this hook has no idea what
  // "GENERATING" even means, and `showGeneratingPlaceholder` above has no
  // idea whether the actual image bytes have decoded.
  const resultThumbnailUrl = hasSuccessImage
    ? resolveCanvasImageThumbUrl(workspaceId, {
        entityId: outputAssetId,
        generatedOutputId: displayOutputId,
        nodeType: 'generate_image',
      })
    : null
  const mediaGate = useCanvasMediaViewportGate()
  const mediaImage = useCanvasProgressiveImageSrc({
    thumbnailUrl: resultThumbnailUrl,
    previewUrl: hasSuccessImage ? resultUrl : null,
    isNearViewport: mediaGate.isNearViewport,
    renderedWidthPx: mediaGate.renderedWidthPx,
    resetKey: displayOutputId || null,
  })
  const currentPresentation = useMemo(
    () =>
      deriveCurrentGenerationPresentation({
        effectiveStatus: submission.effectiveStatus,
        isBusy: submission.isBusy,
        runHydrating,
        hasPersistedOutput: Boolean(generatedOutputId),
        taskTerminalFailed: task?.status === 'failed' || status === 'FAILED',
        taskTerminalSucceeded: task?.status === 'succeeded' || status === 'COMPLETED',
        hasAttemptIdentity: Boolean(nodeData.latestRunId),
        pollSyncError,
      }),
    [
      generatedOutputId,
      nodeData.latestRunId,
      pollSyncError,
      runHydrating,
      status,
      submission.effectiveStatus,
      submission.isBusy,
      task?.status,
    ],
  )
  const showGeneratingPlaceholder =
    (currentPresentation.showSpinner || currentPresentation.showSyncError) && !showActivePreview
  const collapsedMediaResult = !showExpanded && hasSuccessImage

  // VistoeraTASK-00153 §六 — `naturalSize` is only ever WRITTEN by the
  // result `<img onLoad>` below, so it otherwise keeps whichever
  // result's pixel dimensions last decoded. Reset it the instant the
  // DISPLAYED result identity changes (regenerate replacing an old
  // result with a new one) so a stale aspect ratio/box size from the
  // SUPERSEDED result can never drive the new result's layout, even
  // for the one render before the new result's own onLoad fires.
  useEffect(() => {
    setNaturalSize({ w: 0, h: 0 })
  }, [displayOutputId])

  const mediaPresentation = useMemo(() => {
    if (!collapsedMediaResult || !naturalSize.w || !naturalSize.h) return null
    return resolveCollapsedMediaPresentationSize(naturalSize.w, naturalSize.h)
  }, [collapsedMediaResult, naturalSize.h, naturalSize.w])

  const cardWidth = useMemo(
    () =>
      resolveGenerationCardWidth({
        expanded: showExpanded,
        expandedWidthPx: expandedWidth,
        hasGeneratedResult: hasSuccessImage,
        strategy: 'image',
        naturalWidth: naturalSize.w,
        naturalHeight: naturalSize.h,
        aspectRatio: ratioForSubmit(ratio),
        resultCarrierWidthPx:
          collapsedMediaResult && mediaPresentation
            ? mediaPresentation.mediaWidth
            : !showExpanded && hasSuccessImage && naturalSize.w && naturalSize.h
              ? resolveCollapsedMediaCarrierSize(naturalSize.w, naturalSize.h).carrierWidth
              : undefined,
      }),
    [collapsedMediaResult, expandedWidth, hasSuccessImage, mediaPresentation, naturalSize.h, naturalSize.w, ratio, showExpanded],
  )

  const successImageSize = useMemo(() => {
    if (!hasSuccessImage) return null
    if (!showExpanded) {
      if (mediaPresentation) {
        return { width: mediaPresentation.mediaWidth, height: mediaPresentation.mediaHeight }
      }
      if (naturalSize.w && naturalSize.h) {
        const carrier = resolveCollapsedMediaCarrierSize(naturalSize.w, naturalSize.h)
        return { width: carrier.carrierWidth, height: carrier.carrierHeight }
      }
      const availableWidth = Math.max(120, cardWidth - 8)
      return computeImageSuccessDisplaySize(
        naturalSize.w,
        naturalSize.h,
        availableWidth,
        IMAGE_SUCCESS_RESULT_MAX_HEIGHT_COLLAPSED,
      )
    }
    const availableWidth = imageSuccessContentWidth(expandedWidth, true)
    return computeImageSuccessDisplaySize(
      naturalSize.w,
      naturalSize.h,
      availableWidth,
      IMAGE_SUCCESS_RESULT_MAX_HEIGHT_EXPANDED,
    )
  }, [cardWidth, expandedWidth, hasSuccessImage, mediaPresentation, naturalSize.h, naturalSize.w, showExpanded])

  const placeholderBox = useMemo(() => computePreviewBox(ratio), [ratio])
  const resultAreaHeight = hasSuccessImage && successImageSize
    ? imageSuccessResultAreaHeight(successImageSize, showExpanded)
    : showExpanded
      ? IMAGE_BUSY_PLACEHOLDER_MIN_HEIGHT_EXPANDED
      : Math.min(IMAGE_SUCCESS_RESULT_MAX_HEIGHT_COLLAPSED, placeholderBox.height)

  const handleTop = (HEADER_HEIGHT + resultAreaHeight) / 2

  // VistoeraTASK-00144 (§三/§四/§五) — when Related Assets is VISIBLY
  // open, each chip mounts its OWN real, one-to-one `<Handle>` (keyed by
  // the edge's OWN stable `edgeId`, never array index) inside
  // `ImageRelatedAssetsSection`/`InputMaterialsRow`; otherwise (whole
  // node collapsed, OR expanded but the Related Assets sub-section is
  // itself collapsed) every reference edge instead gets a
  // "legacy-hidden" fallback anchor at the SAME `handleTop` the always-
  // present aggregate handle uses, mirroring `GenerateVideoNode.tsx`'s
  // established collapsed-aggregate pattern exactly (TASK-00143) —
  // toggling this NEVER deletes/recreates an edge or touches its
  // `target_handle`/binding, only which DOM anchor currently renders
  // that SAME id.
  const inputHandleVisibility = useMemo(
    () => resolveGenerateImageInputHandleVisibility(showExpanded, relatedAssetsOpen),
    [relatedAssetsOpen, showExpanded],
  )
  const relatedAssetsDropClass = relatedAssetsDropZoneClassName(showExpanded, mediaDropState, imageMaterials.length)
  const stackedPerEdgeTargetHandleIds = useMemo(
    () => imageMaterials.map((m) => imageReferenceAssetHandleId(m.edgeId)),
    [imageMaterials],
  )
  // Stable (edge-id-keyed, order-independent-enough) dependency so the
  // node-internals recompute effect below only re-fires when the SET of
  // connected reference-image edges actually changes, not on every
  // unrelated render (§十八 perf — avoid needless global re-measurement).
  const imageMaterialEdgeIdsKey = useMemo(() => imageMaterials.map((m) => m.edgeId).join('|'), [imageMaterials])

  const runLabel = useMemo(
    () =>
      generationFooterRunLabel(currentPresentation.displayStatus, t, {
        hasResult,
        media: 'image',
      }),
    [currentPresentation.displayStatus, hasResult, t],
  )

  const resultStatusBadge = useMemo(
    () =>
      generationResultPlaceholderLabel(currentPresentation.displayStatus, t, {
        runHydrating,
        showActivePreview,
      }),
    [currentPresentation.displayStatus, runHydrating, showActivePreview, t],
  )

  const mediaDropClass =
    mediaDropState === 'candidate'
      ? ' is-drop-candidate'
      : mediaDropState === 'incompatible' || mediaDropState === 'reference-limit'
        ? ' is-drop-incompatible'
        : ''

  useEffect(() => {
    // VistoeraTASK-00144 (§十七) — per-asset handles mount/unmount
    // whenever `showRealPerAssetHandles` flips or the connected-edge SET
    // changes, so both must be in this dependency array alongside the
    // pre-existing triggers; still the SAME canonical `requestAnimationFrame`
    // lifecycle, never a `setTimeout`.
    const frame = window.requestAnimationFrame(() => updateNodeInternals(id))
    return () => window.cancelAnimationFrame(frame)
  }, [
    cardWidth,
    id,
    imageMaterialEdgeIdsKey,
    relatedAssetsOpen,
    resultAreaHeight,
    showExpanded,
    inputHandleVisibility.showExpandedSlotStackedPerEdgeHandles,
    stackedPerEdgeTargetHandleIds.join('|'),
    updateNodeInternals,
  ])

  const taskFailurePresentation = useMemo(() => {
    if (!currentPresentation.showFailedResult || task?.status !== 'failed') return null
    return localizeImageGenerationTaskError(
      { errorCode: task.error_code, errorMessage: task.error_message, taskId: task.id },
      t,
    )
  }, [currentPresentation.showFailedResult, t, task])
  const taskFailureMessage = taskFailurePresentation
    ? taskFailurePresentation.taskId
      ? `${taskFailurePresentation.message} (${t('generation.errors.taskIdHint', { taskId: taskFailurePresentation.taskId })})`
      : taskFailurePresentation.message
    : null
  const syncErrorMessage =
    currentPresentation.showSyncError && nodeData.latestRunId
      ? `${t('generation.errors.statusSyncFailed')} (${t('generation.errors.taskIdHint', { taskId: nodeData.latestRunId })})`
      : currentPresentation.showSyncError
        ? t('generation.errors.statusSyncFailed')
        : null
  const batchErrorSummary =
    typeof fields[IMAGE_GENERATION_BATCH_ERROR_SUMMARY_FIELD] === 'string'
      ? (fields[IMAGE_GENERATION_BATCH_ERROR_SUMMARY_FIELD] as string)
      : null
  const footerErrorMessage = currentPresentation.showFooterError
    ? taskFailureMessage || syncErrorMessage || submitError?.message || batchErrorSummary || null
    : null
  const collapsedError = !showExpanded && Boolean(footerErrorMessage)
  const showExternalQuickActions = Boolean(showActivePreview)
  const previousResultUrl = previousOutputId ? imageGenerationOutputContentUrl(workspaceId, previousOutputId) : null

  return (
    <div
      ref={mediaGate.setRef}
      className={`pcv-node pcv-node--media pcv-node--generator pcv-node--generate-image${selected ? ' is-selected' : ''} ${canvasNodeShellStateClass(showExpanded)} ${generationSlotIngressShellClass(showExpanded)}${resultZoneActive ? ' is-result-zone-active' : ''}${collapsedMediaResult ? ' is-collapsed-media-result' : ''}`}
      style={
        {
          width: cardWidth,
          // VistoeraTASK-00144 (§七/§八 — Problem B) — exposes the SAME
          // JS-computed, expand-state-independent Result-viewport
          // vertical-center pixel the left/right `<Handle>`s already
          // use, so the external Quick Actions rail's CSS `top` can
          // reference it via `var(--pcv-rail-top)` instead of a
          // `calc(100% ...)` formula that only happens to be correct
          // while collapsed (`production-canvas.css`).
          '--pcv-rail-top': `${handleTop}px`,
        } as CSSProperties
      }
      data-testid="canvas-image-node"
      data-node-id={id}
      data-expanded={showExpanded ? 'yes' : 'no'}
      data-canonical-order="Header|Result|RelatedAssets|Parameters|Prompt|Footer"
      data-prompt-after-parameters="yes"
    >
      {/* VistoeraTASK-00333 — collapsed: canonical left-center aggregate
       * input; expanded: the SAME `GENERIC_INPUT_HANDLE` id moves into
       * `ImageRelatedAssetsSection` (never two visible anchors). */}
      {inputHandleVisibility.showCollapsedStandardInputHandle ? (
        <Handle
          type="target"
          position={Position.Left}
          id={GENERIC_INPUT_HANDLE}
          className="pcv-handle pcv-handle--lg"
          style={{ top: handleTop }}
          data-testid="image-collapsed-aggregate-in-handle"
        />
      ) : null}
      {inputHandleVisibility.showLegacyHiddenReferenceAnchors
        ? imageMaterials.map((m) => (
            // VistoeraTASK-00144 (§三/§四, TASK-00143 collapsed-aggregate
            // precedent) — one legacy-hidden, real-id, non-destructive
            // fallback anchor PER connected reference-image edge so its
            // ALREADY-scoped `target_handle` (`imageReferenceAssetHandleId`,
            // assigned at read-time hydration / edge-creation, never
            // mutated here) always resolves to a real DOM position — at
            // the SAME `handleTop` the aggregate handle uses — whenever
            // its OWN chip-owned real handle (below, inside
            // `ImageRelatedAssetsSection`) is not currently mounted.
            <Handle
              key={`collapsed-ref::${m.edgeId}`}
              type="target"
              position={Position.Left}
              id={imageReferenceAssetHandleId(m.edgeId)}
              className="pcv-handle pcv-handle--structured-asset pcv-handle--legacy-hidden"
              style={{ top: handleTop }}
            />
          ))
        : null}

      <div className="pcv-node-inner">
        {/* ① Header — icon + title (+ runtime status); keep actions */}
        <CanvasFunctionCardHeader
          className="pcv-gen-image-header"
          variant={collapsedMediaResult ? 'media' : 'brief'}
          icon={def.icon}
          color={def.color}
          title={displayLabel}
          statusBadge={
            collapsedError ? (
              <span className="pcv-media-error-badge" title={footerErrorMessage || t('media.failedFull')}>
                {t('media.failed')}
              </span>
            ) : null
          }
          actions={
            <>
              <Tooltip title={pinned ? t('board.unpinExpanded') : t('board.pinExpanded')} mouseEnterDelay={0.3}>
                <button
                  type="button"
                  className={`pcv-media-hover-btn pcv-pin-btn${pinned ? ' is-pinned' : ''}`}
                  aria-label={pinned ? t('board.unpinExpanded') : t('board.pinExpanded')}
                  aria-pressed={pinned}
                  data-testid="image-pin-toggle"
                  onClick={(e) => {
                    e.stopPropagation()
                    togglePin()
                  }}
                >
                  {pinned ? <PushpinFilled /> : <PushpinOutlined />}
                </button>
              </Tooltip>
              {showActivePreview ? (
                <Tooltip title={mediaHeader.download} mouseEnterDelay={0.3}>
                  <button type="button" className="pcv-media-hover-btn" aria-label={mediaHeader.download} onClick={handleDownload}>
                    <DownloadOutlined />
                  </button>
                </Tooltip>
              ) : null}
              <Tooltip title={t('generation.historyVersions')} mouseEnterDelay={0.3}>
                <button
                  type="button"
                  ref={historyBtnRef}
                  className="pcv-media-hover-btn"
                  aria-label={t('generation.historyVersions')}
                  onClick={(e) => {
                    e.stopPropagation()
                    setHistoryOpen(true)
                  }}
                >
                  <HistoryOutlined />
                </button>
              </Tooltip>
              <Tooltip title={mediaHeader.duplicate} mouseEnterDelay={0.3}>
                <button type="button" className="pcv-media-hover-btn" aria-label={mediaHeader.duplicate} onClick={handleCopyClick}>
                  <CopyOutlined />
                </button>
              </Tooltip>
              <Tooltip
                title={deleteLockedByRun ? t('generation.deleteLockedRunningSlot') : mediaHeader.delete}
                mouseEnterDelay={0.3}
              >
                <button
                  type="button"
                  className="pcv-media-hover-btn pcv-media-hover-btn--danger"
                  aria-label={mediaHeader.delete}
                  aria-disabled={deleteLockedByRun || undefined}
                  disabled={deleteLockedByRun}
                  style={deleteLockedByRun ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                  onClick={handleDeleteClick}
                >
                  <CloseOutlined />
                </button>
              </Tooltip>
            </>
          }
        />

        {/* ② Result — always visible */}
        <div
          className={`pcv-gen-result-area pcv-gen-image-result-area${hasSuccessImage ? ' has-success-image' : ''}${mediaDropClass} ${NODE_EXPAND_TRIGGER_CLASS}`}
          data-testid="image-result-area"
          onMouseEnter={activateResultZone}
          onMouseLeave={deactivateResultZone}
          onClick={onMediaSingleClick}
          onDoubleClick={showActivePreview ? onMediaDoubleClick : undefined}
        >
          {showActivePreview && resultUrl && successImageSize ? (
            <div
              className="pcv-gen-image-result-frame is-content-fit"
              style={
                collapsedMediaResult
                  ? { width: '100%', height: successImageSize.height }
                  : { width: successImageSize.width, height: successImageSize.height }
              }
              data-testid="image-result-success-frame"
            >
              {mediaImage.src ? (
                <img
                  className="pcv-gen-image-result-img"
                  src={mediaImage.src}
                  alt=""
                  draggable={false}
                  onLoad={(e) => {
                    // VistoeraTASK-00153 §六 — identity guard, not a
                    // `setTimeout`: a late `onLoad` firing for a src the
                    // hook has already superseded (e.g. a very fast
                    // second regenerate) must never resurrect stale
                    // pixel dimensions for the CURRENT result. Compare
                    // against the hook's own current committed src —
                    // the single source of truth for "what SHOULD be
                    // showing right now" — before applying either state
                    // update.
                    if (e.currentTarget.currentSrc !== mediaImage.src && e.currentTarget.src !== mediaImage.src) return
                    setNaturalSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })
                    mediaImage.markLoaded()
                  }}
                  onError={(e) => {
                    if (e.currentTarget.currentSrc !== mediaImage.src && e.currentTarget.src !== mediaImage.src) return
                    mediaImage.markError()
                  }}
                />
              ) : null}
              {/* VistoeraTASK-00147 §六/§七/§十一 — GENERATION already
               * SUCCEEDED (that's the only way this branch renders at
               * all) but the actual media bytes for THIS node aren't
               * near-viewport-gated-loaded/decoded yet — an explicit
               * "正在加载" MEDIA state, never "正在生成"/`showGeneratingPlaceholder`
               * (that flag is GENERATION-status-only and is already
               * `false` here). */}
              {mediaGate.isNearViewport && (!mediaImage.src || mediaImage.isLoading) ? (
                <div className="pcv-media-loading-overlay" data-testid="image-result-media-loading">
                  <LoadingOutlined spin />
                  <span>{t('mediaCard.imageLoading')}</span>
                </div>
              ) : null}
              {currentPresentation.showFailedResult || mediaImage.hasError ? (
                <div className="pcv-media-failure-overlay">
                  <span>{mediaImage.hasError ? t('mediaCard.imageLoadFailed') : t('media.failedFull')}</span>
                </div>
              ) : null}
            </div>
          ) : showGeneratingPlaceholder ? (
            <div className="pcv-gen-result-loading" style={{ minHeight: resultAreaHeight }}>
              <LoadingOutlined spin />
              <span>{resultStatusBadge}</span>
            </div>
          ) : currentPresentation.showFailedResult ? (
            <div className="pcv-gen-result-empty" style={{ minHeight: resultAreaHeight }} data-testid="image-result-failed">
              <span className="pcv-gen-result-empty-icon">
                <PictureOutlined />
              </span>
              <span>{footerErrorMessage || t('media.failedFull')}</span>
            </div>
          ) : (
            <div className="pcv-gen-result-empty" style={{ minHeight: resultAreaHeight }} data-testid="image-result-empty">
              <span className="pcv-gen-result-empty-icon">
                <PictureOutlined />
              </span>
              <span>{t('generation.imageResultEmptyTitle')}</span>
              <span className="pcv-gen-result-empty-hint">
                {mediaDropState === 'reference-limit'
                  ? referenceLimitValidationMessage(imageReferenceCapability, t)
                  : mediaDropState === 'candidate'
                    ? t('media.dropHere')
                    : showExpanded
                      ? t('generation.imageResultEmptyHintExpanded')
                      : t('generation.imageResultEmptyHintCollapsed')}
              </span>
            </div>
          )}
        </div>

        {showExpanded ? (
          <div className="pcv-brief-body pcv-gen-input-body nodrag" data-testid="image-expanded-body" onClick={(e) => e.stopPropagation()}>
            {/* VistoeraTASK-00251 — "编辑图片" entry, between Result and
             * Related Assets (per spec's own screenshot placement — never
             * the Provider/Skill/Generate footer, never a hover-only
             * image overlay, never inside Related Assets). Only rendered
             * once there is a valid, displayed, completed result to edit
             * (`hasSuccessImage` — the SAME gate the result `<img>` itself
             * uses, so idle/queued/running/uploading/failed-without-a-
             * valid-history states never show it; a FAILED node that
             * still has a last-good result stays editable, matching this
             * card's own existing "failed but keep showing last result"
             * semantics rather than a UI guess). Secondary-weight
             * (default `Button`, never `type="primary"`) so it never
             * outranks the footer's "生成/重新生成" CTA. */}
            {hasSuccessImage ? (
              <Button
                block
                icon={<EditOutlined />}
                loading={editorSourcePreparing}
                onClick={handleEditImageClick}
                disabled={!workspaceId || !siteId}
                className="nodrag"
                data-testid="image-edit-image-button"
              >
                {t('mediaCard.editImage')}
              </Button>
            ) : null}

            {/* ③ Related Assets */}
            <ImageRelatedAssetsSection
              workspaceId={workspaceId}
              materials={inputMaterials}
              open={relatedAssetsOpen}
              onToggleOpen={() => setRelatedAssetsOpen((v) => !v)}
              nodeExpanded={showExpanded}
              aggregateInputHandleId={GENERIC_INPUT_HANDLE}
              showSectionAggregateHandle={inputHandleVisibility.showExpandedSectionAggregateHandle}
              dropZoneClassName={relatedAssetsDropClass}
              stackedPerEdgeTargetHandleIds={stackedPerEdgeTargetHandleIds}
              onDisconnect={(edgeId) => nodeData.onDisconnectMaterial?.(id, edgeId)}
              onSelectSource={(nodeId) => nodeData.onSelectNode?.(nodeId)}
              onEditText={(nodeId) => nodeData.onRequestEditText?.(nodeId)}
              onAddClick={openRelatedAssetsPicker}
              addDisabled={!siteId}
            />

            {/* ④ Generation Parameters — BEFORE prompt */}
            <div className="pcv-gen-params-header" data-testid="image-parameters">
              <span className="pcv-brief-section-label">{t('generation.parametersSection')}</span>
              <button type="button" className="pcv-brief-collapse-toggle nodrag" onClick={() => setShowMoreParams((v) => !v)}>
                {showMoreParams ? <DownOutlined /> : <RightOutlined />} {t('generation.moreParams')}
              </button>
            </div>
            {productionFormatLocked ? (
              <div
                className="pcv-storyboard-production-format nodrag"
                data-testid="image-production-format-lock"
                style={{ marginBottom: 8 }}
              >
                <div className="pcv-storyboard-production-format-header">
                  <span className="pcv-brief-section-label">成片规格</span>
                  {formatSourceLabel ? (
                    <span className="pcv-brief-section-optional">🔒 {formatSourceLabel}</span>
                  ) : (
                    <span className="pcv-brief-section-optional">🔒 已锁定</span>
                  )}
                </div>
                <div className="pcv-storyboard-production-format-summary">
                  {lockedAspect}
                  {lockedResolution ? ` · ${lockedResolution}` : ''}
                  {canonicalTarget
                    ? ` · 目标 ${canonicalTarget.width}×${canonicalTarget.height}`
                    : productionTargetWidth && productionTargetHeight
                      ? ` · 目标 ${productionTargetWidth}×${productionTargetHeight}`
                      : ''}
                </div>
                {nativeSize ? (
                  <div className="pcv-brief-section-optional">
                    Provider 原生请求尺寸 {nativeSize.width}×{nativeSize.height}（同长宽比，不改变成片规格）
                  </div>
                ) : null}
                {formatMismatch ? (
                  <div className="pcv-gen-capability-warning" data-testid="image-format-stale-banner">
                    成片规格已改变，需要重新生成关键帧。
                  </div>
                ) : deliveryRenderStale ? (
                  <div className="pcv-gen-capability-warning" data-testid="image-delivery-render-stale">
                    交付清晰度已改变，可保留 AI 源素材并在合片时重新 conform。
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className="pcv-gen-params-primary pcv-gen-image-params-grid">
              {!productionFormatLocked ? (
                <>
                  <div className="pcv-gen-field-row">
                    <span className="pcv-gen-field-label">{t('generation.paramAspectRatio')}</span>
                    <ImageAspectRatioSelect value={ratio} onChange={(v) => patchFields({ ratio: v })} />
                  </div>
                  <div className="pcv-gen-field-row">
                    <span className="pcv-gen-field-label">{t('generation.paramQuality')}</span>
                    <CanvasSelect
                      size="small"
                      className="pcv-gen-field-select nodrag"
                      value={quality}
                      onChange={(v) => patchFields({ quality: v })}
                      options={qualityOptions}
                      popupMatchSelectWidth={false}
                      getPopupContainer={() => document.body}
                    />
                  </div>
                </>
              ) : null}
              <div className="pcv-gen-field-row">
                <span className="pcv-gen-field-label">{t('generation.paramImageCount')}</span>
                <CanvasSelect
                  size="small"
                  className="pcv-gen-field-select nodrag"
                  value={candidateCount}
                  onChange={(v) => patchFields({ candidateCount: v })}
                  options={imageCountOptions}
                  popupMatchSelectWidth={false}
                  getPopupContainer={() => document.body}
                />
              </div>
            </div>

            {showMoreParams ? (
              <div className="pcv-brief-more-body">
                {hasReferences ? (
                  <div className="pcv-gen-field-row">
                    <span className="pcv-gen-field-label">{t('generation.paramStrength')}</span>
                    <CanvasSelect
                      size="small"
                      className="pcv-gen-field-select nodrag"
                      value={strength}
                      onChange={(v) => patchFields({ strength: v })}
                      options={[0.5, 0.65, 0.75, 0.85, 0.95].map((v) => ({
                        value: v,
                        label: t('generation.strengthLabel', { value: v }),
                      }))}
                      popupMatchSelectWidth={false}
                      getPopupContainer={() => document.body}
                    />
                  </div>
                ) : (
                  <div className="pcv-gen-capability-warning">{t('generation.moreParamsEmptyHint')}</div>
                )}
              </div>
            ) : null}

            {/* ⑤ Prompt — AFTER parameters */}
            <div
              className="pcv-brief-section pcv-image-prompt-section"
              data-testid="image-prompt-section"
              data-image-input-port={IMAGE_PROMPT_IN_HANDLE}
            >
              <VideoInputSectionHandle
                id={IMAGE_PROMPT_IN_HANDLE}
                className="pcv-handle pcv-handle--prompt"
                testId="image-prompt-in-handle"
              />
              <span className="pcv-brief-section-label">{t('generation.promptSection')}</span>
              <div data-testid="image-prompt-editor">
                <AssetMentionPromptEditor
                  value={prompt}
                  onChange={(next) => patchFields({ prompt: next })}
                  aliasByEdgeId={aliasByEdgeId}
                  thumbUrlByEdgeId={thumbUrlByEdgeId}
                  placeholder={t('generation.promptPlaceholderImage')}
                />
              </div>
              <div
                className={`pcv-gen-prompt-counter${promptLimitState.exceedsLimit ? ' pcv-gen-prompt-counter--error' : promptLimitState.nearLimit ? ' pcv-gen-prompt-counter--warn' : ''}`}
                data-testid="image-prompt-length-counter"
              >
                {promptLimitState.limitKnown
                  ? t('generation.promptLengthCounter', {
                      current: promptLimitState.length.toLocaleString(),
                      max: promptLimitState.maxCharacters?.toLocaleString() ?? '',
                    })
                  : t('generation.promptLengthOnly', { current: promptLimitState.length.toLocaleString() })}
              </div>
              <div className="pcv-gen-prompt-tools-row nodrag">
                <ImagePromptTranslateTools
                  workspaceId={workspaceId}
                  prompt={prompt}
                  onPromptChange={(next) => patchFields({ prompt: next })}
                  visible={showPromptTranslate}
                  disabled={isBusy}
                />
                <div className="pcv-gen-prompt-tools">
                  <Tooltip title={t('media.comingSoon')}>
                    <button type="button" className="pcv-gen-tool-btn" disabled>
                      {t('generation.promptOptimize')}
                    </button>
                  </Tooltip>
                  <Tooltip title={t('media.comingSoon')}>
                    <button type="button" className="pcv-gen-tool-btn" disabled>
                      {t('generation.photoStyle')}
                    </button>
                  </Tooltip>
                  <button type="button" className="pcv-gen-tool-btn" disabled={!prompt} onClick={() => patchFields({ prompt: '' })}>
                    {t('generation.clearPrompt')}
                  </button>
                </div>
              </div>
            </div>

            {referenceLimitMessage ? (
              <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">{referenceLimitMessage}</div>
            ) : null}
            {promptLimitMessage ? (
              <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">{promptLimitMessage}</div>
            ) : null}
            {providerExecutionBlocked ? (
              <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error" data-testid="image-provider-execution-block">
                {providerExecutionBlockMessage}
              </div>
            ) : null}
            {nodeAuthDenied ? (
              <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error" data-testid="image-node-auth-denied">
                当前账号无权使用该功能节点
              </div>
            ) : null}
            {!nodeAuthDenied && (providerAuthDenied || noAllowedImageProviders) ? (
              <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error" data-testid="image-provider-auth-denied">
                {noAllowedImageProviders ? '当前账号暂无可用执行通道' : '当前账号无权使用所选执行通道'}
              </div>
            ) : null}
            {disconnectedMentionCount > 0 ? (
              <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">
                {t('generation.disconnectedMentionsCount', { count: disconnectedMentionCount })}
              </div>
            ) : null}
            {driftInspectorText && !isBusy ? (
              <div className="pcv-gen-capability-warning pcv-gen-capability-warning--stale">{driftInspectorText}</div>
            ) : null}
            {taskFailureMessage ? (
              <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error" data-testid="image-task-failure">
                {taskFailureMessage}
              </div>
            ) : footerErrorMessage && !taskFailureMessage ? (
              <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error" data-testid="image-submit-failure">
                {footerErrorMessage}
              </div>
            ) : null}

            {/* ⑥ Footer — Provider | Skill | Generate (no Diverge; capability unsupported) */}
            <div className="pcv-gen-footer nodrag" data-testid="image-footer" onClick={(e) => e.stopPropagation()}>
              <Tooltip
                title={
                  providerChannelUnresolved
                    ? t('generation.errorProviderChannelUnavailable')
                    : t('media.providerChannel')
                }
                mouseEnterDelay={0.4}
              >
                <CanvasSelect
                  size="small"
                  className="pcv-gen-field-select pcv-gen-footer-select nodrag"
                  data-testid="image-provider-select"
                  status={providerChannelUnresolved || providerAuthDenied ? 'error' : undefined}
                  value={
                    selectableProviderChannels.some((c) => c.id === providerChannel)
                      ? providerChannel
                      : undefined
                  }
                  placeholder={t('media.providerChannel')}
                  onChange={(v) => handleProviderChange(v)}
                  options={selectableProviderChannels.map((channel) => ({
                    value: channel.id,
                    label: channel.label,
                    title: channel.billing_tooltip || channel.billing_hint,
                  }))}
                  disabled={nodeAuthDenied || noAllowedImageProviders}
                  popupMatchSelectWidth={false}
                  getPopupContainer={() => document.body}
                />
              </Tooltip>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select pcv-gen-footer-select nodrag"
                data-testid="image-skill-select"
                value={skillSelectionSource === 'EXPLICIT' ? IMAGE_SKILL_KEY : 'AUTO'}
                options={[
                  { value: 'AUTO', label: t('generation.skillAuto') },
                  { value: IMAGE_SKILL_KEY, label: t('generation.skillImageGeneration') },
                ]}
                onChange={(v) =>
                  patchFields(
                    v === 'AUTO'
                      ? { skillSelectionSource: 'AUTO', skillKey: null, skillVersion: null }
                      : { skillSelectionSource: 'EXPLICIT', skillKey: IMAGE_SKILL_KEY, skillVersion: IMAGE_SKILL_VERSION },
                  )
                }
                popupMatchSelectWidth={false}
                getPopupContainer={() => document.body}
              />
              <button
                ref={runBtnRef}
                type="button"
                className="pcv-gen-footer-generate-btn"
                data-testid="image-generate-submit"
                disabled={
                  isBusy ||
                  providerExecutionBlocked ||
                  nodeAuthDenied ||
                  providerAuthDenied ||
                  noAllowedImageProviders ||
                  !resolvedPrompt.trim() ||
                  referenceLimitState.exceedsLimit ||
                  promptLimitState.exceedsLimit
                }
                onClick={handleGenerateClick}
              >
                {isBusy ? <LoadingOutlined spin /> : null} {runLabel}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <Handle type="source" position={Position.Right} id="image-out" className="pcv-handle pcv-handle--lg" style={{ top: handleTop }} />

      {/* External Quick Actions — CARD RIGHT OUTSIDE (sibling of inner; not in layout width) */}
      {showExternalQuickActions ? (
        <MediaWorkflowRail
          sourceNodeType="generate_image"
          className="pcv-media-workflow-toolbar--generate-image"
          onMouseEnter={activateResultZone}
          onMouseLeave={deactivateResultZone}
          onQuickGenerate={(kind) => {
            nodeData.onQuickGenerate?.(id, kind)
          }}
        />
      ) : null}

      <CanvasActionPopover
        anchorEl={historyBtnRef.current}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={t('generation.historyVersions')}
        width={240}
      >
        <div className="pcv-candidate-expand-popover">
          {previousResultUrl ? (
            <>
              <span style={{ fontSize: 11, color: 'var(--pcv-text-tertiary)' }}>{t('generation.historyPrevious')}</span>
              <img src={previousResultUrl} alt="" style={{ width: '100%', borderRadius: 8, marginTop: 8 }} />
            </>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--pcv-text-tertiary)' }}>{t('generation.historyEmpty')}</span>
          )}
          {showActivePreview ? (
            <button type="button" className="pcv-action-popover-btn" style={{ marginTop: 8 }} onClick={handleDownload}>
              {mediaHeader.download}
            </button>
          ) : null}
        </div>
      </CanvasActionPopover>

      <CanvasActionPopover anchorEl={runBtnRef.current} open={regenerateChoiceOpen} onClose={() => setRegenerateChoiceOpen(false)} title={regenerate.title}>
        <p className="pcv-action-popover-desc">{t('generation.regenerateDescription')}</p>
        <div className="pcv-action-popover-actions">
          <button type="button" className="pcv-action-popover-btn" onClick={handleRegenerateSeparately}>
            {regenerate.separate}
          </button>
          <button type="button" className="pcv-action-popover-btn pcv-action-popover-btn--primary" onClick={handleOverwriteRegenerate}>
            {regenerate.overwrite}
          </button>
        </div>
      </CanvasActionPopover>

      <MediaLightbox
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        kind="image"
        src={showActivePreview ? resultUrl : null}
        masterSrc={showActivePreview ? masterUrl : null}
        masterMeta={
          showActivePreview
            ? {
                // Never pass canvas-preview natural size — viewer fills master
                // dimensions after GENERATED_MASTER decode / asset metadata.
                origin: 'generated',
              }
            : null
        }
      />

      {/* VistoeraTASK-00251 — mounted ONLY while `editorOpen` (never
       * merely while expanded), exactly mirroring `ImageAssetNode.tsx`'s
       * own mount gate: the heavy `CanvasImageEditorOverlay`/
       * `DualPaneCutoutEditor` chunk/runtime is never paid for until this
       * SPECIFIC node's "编辑图片" is actually clicked, and fully unmounts
       * (never `display:none`) the instant it closes. Same Editor
       * Stability P0 boundary + `editorInstanceKey` remount contract as
       * `ImageAssetNode` — see that file's doc comments for the full
       * "why" of both. */}
      {editorOpen && editorSourceAssetId && workspaceId && siteId ? (
        <Suspense fallback={null}>
          <ImageEditorErrorBoundary
            key={editorInstanceKey}
            onClose={() => setEditorOpen(false)}
            onRetry={handleEditorRetry}
            diagnosticsRef={editorDiagnosticsRef}
          >
            <CanvasImageEditorOverlay
              workspaceId={workspaceId}
              siteId={siteId}
              sourceAssetId={editorSourceAssetId}
              sourceLabel={displayLabel}
              sourceCanvasId={projectId}
              sourceNodeId={id}
              // Already-known provider-native pixel dimensions for THIS
              // displayed result (persisted at generation-completion time,
              // see `applyCompletedTask`'s `provider_native_width/height`
              // patch above) — enables the same fast placeholder-then-
              // full-resolution editor load path `ImageAssetNode` gets
              // from its own `masterMeta` fetch; falls back to today's
              // single-fetch behavior if unknown for this result.
              knownSourceWidth={(fields.provider_native_width as number | null | undefined) ?? null}
              knownSourceHeight={(fields.provider_native_height as number | null | undefined) ?? null}
              onClose={() => setEditorOpen(false)}
              onSavedAsNewAsset={handleEditorSavedAsNewAsset}
              diagnosticsRef={editorDiagnosticsRef}
            />
          </ImageEditorErrorBoundary>
        </Suspense>
      ) : null}
    </div>
  )
}

export default memo(GenerateImageNodeImpl)
